import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError, addToTeam, createTeam, editIntentAnswers, excludeIntent, exportIntents,
  getIntentAnswers,
  getProjectIntents, getProjects, importAnswers, getProposal, moveMember,
  publishTeams, putProjectForm, removeTeam, renameTeam, requestProposal, syncRoster, unpublishTeams,
  type ProjectFormConfig, type ProjectQuestion, type ProjectsOverview, type ProposalDetail,
  type ProposalTeam,
  type PromptPreview, type TeacherIntentRow, type TeamRule, type ApplyProjectBody,
  type EditableAnswers, type Unplaced,
} from "../api";
import { Link, Navigate, useParams } from "react-router-dom";
import { Deadline } from "../Deadline";
import { useShell } from "../Layout";
import { Elapsed, Spin, useLive } from "../live";
import { ProjectQuestions } from "../ProjectQuestions";
import { QuestionEditor } from "../QuestionEditor";
import { TeamNameForm } from "../TeamName";
import { TEAM_TABS, isTeamTab, routes, type TeamTab } from "../routes";
import * as csv from "../csv";
import { tracked, useAsync } from "../useAsync";
import { Chips, QCard, ZoneChips } from "./Apply";
import {
  Badge, Card, CardBody, CardHead, DASH, Empty, Freshness, Kpi, Pane, State, When, usd,
} from "../ui";

/**
 * Project teams: what each enrolled student wants to build, and the grouping made from it.
 *
 * 🔴 THIS IS THE ONLY ROUND THAT GROUPS, and it lives under Students because its audience is
 * the roster. The Applicants page decides who joins a cohort and builds no teams; a proposal
 * carries no `kind` because there is nothing for it to be distinguished from.
 *
 * 🔴 A FORM OPEN OVER AN UNSYNCED ROSTER REFUSES EVERY STUDENT WHO OPENS IT, and the counts
 * alone render that as "nobody has replied yet" — the same shape as a cohort that is simply
 * slow. The roster reading is a STOP banner above everything else, with the button that fixes
 * it, because a form that cannot work is not a state to leave somebody guessing about.
 *
 * ⚠️ TWO CAPABILITIES, NOT ONE. Everything that reaches no student — the form, the answers,
 * setting people aside, requesting a proposal and moving people — is Applicants & teams
 * (`can_manage_teams`); publishing tells people who they are working with (`can_release`).
 * The buttons are disabled separately and say which is missing, because a control that is
 * simply absent reads as a feature that does not exist.
 * 🔴 AND A CHANGE TO THE PUBLISHED PLAN IS BOTH (owner, 2026-09-28): students are reading it,
 * so moving somebody on it tells two teams who they are working with. `mayChange` is the one
 * place that says so, and the server asks for the same pair.
 */
/**
 * What changed, and which tab it belongs to.
 *
 * 🔴 ONE SAVE BAR ACROSS THREE TABS ONLY WORKS IF THE COUNT SAYS WHERE. A teacher edits the
 * closing date, switches to Questions, adds a question, and presses one button — the bar has
 * to name both or the count is a number they cannot verify. Derived, never stored.
 *
 * ⚠️ A QUESTION'S `grouping` FLAG COUNTS AGAINST **GROUPING RULES**, NOT QUESTIONS, EVEN
 * THOUGH IT LIVES IN THE QUESTION. It is one field with two surfaces — the drawer's "The
 * grouping reads this" and the Grouping-rules switch write the same `questions[i].grouping` on
 * the same draft — and the dot has to point at where somebody most likely changed it.
 */
export function countChanges(
  loadedForm: ProjectFormConfig, loadedQs: ProjectQuestion[],
  draftForm: ProjectFormConfig, draftQs: ProjectQuestion[],
): { questions: number; settings: number; grouping: number; total: number } {
  const SETTINGS = ["closes_at", "team_size_min", "team_size_max", "intro"] as const;
  let settings = 0;
  for (const k of SETTINGS) if (loadedForm[k] !== draftForm[k]) settings += 1;
  // ⚠️ EVERY FIELD THE GROUPING-RULES TAB EDITS, OR THE BAR UNDERCOUNTS. It names what was
  // written and is the only confirmation a save gives — so an edit it cannot see saves
  // silently, and the next person to look cannot tell whether it took.
  let grouping = loadedForm.guidance !== draftForm.guidance ? 1 : 0;
  if ((loadedForm.model ?? "") !== (draftForm.model ?? "")) grouping += 1;
  let questions = 0;
  const n = Math.max(loadedQs.length, draftQs.length);
  for (let i = 0; i < n; i += 1) {
    const a = loadedQs[i]; const b = draftQs[i];
    if (!a || !b) { questions += 1; continue; }
    const { grouping: ag, ...ar } = a;
    const { grouping: bg, ...br } = b;
    if (JSON.stringify(ar) !== JSON.stringify(br)) questions += 1;
    if (ag !== bg) grouping += 1;
  }
  return { questions, settings, grouping, total: questions + settings + grouping };
}

/** "two here, one in Settings" — the sentence that makes one bar trustworthy from any tab. */
function whereChanged(c: ReturnType<typeof countChanges>, tab: TeamTab): string {
  const NAME: Partial<Record<TeamTab, string>> = {
    questions: "Questions", settings: "Settings", grouping: "Grouping rules",
  };
  const here = (c as unknown as Record<string, number>)[tab] ?? 0;
  const elsewhere = (Object.keys(NAME) as TeamTab[])
    .filter((k) => k !== tab && ((c as unknown as Record<string, number>)[k] ?? 0) > 0)
    .map((k) => `${(c as unknown as Record<string, number>)[k]} in ${NAME[k]}`);
  const parts = [here > 0 ? `${here} here` : "none in here", ...elsewhere];
  return parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
}

export function TeamsPage() {
  const { courseId, access } = useShell();
  const { tab: rawTab } = useParams();
  const overview = useAsync(() => getProjects(courseId), [courseId]);
  // 🔴 A GROUPING THAT ENDS IS READ AT ONCE (2026-10-06). The page asked once and never again, so
  // a run that finished while somebody watched went on reading "queued" until a refresh.
  const live = useLive();
  const lastGrouped = useRef(0);
  const reloadProjects = overview.reload;
  useEffect(() => {
    const newest = live.grouped[live.grouped.length - 1]?.n ?? 0;
    if (newest > lastGrouped.current) { lastGrouped.current = newest; reloadProjects(); }
  }, [live.grouped, reloadProjects]);
  const [openProposal, setOpenProposal] = useState<string>("");
  // 🔴 THE PLAN IS OPEN WHEN THE TAB IS (owner, 2026-09-23): *"when we open teams also appear
  // the curretn team!"*. Landing on a table of one row and having to press Review to see the
  // teams put the page's whole subject one click away. The published plan wins, otherwise the
  // newest — and `touched` means a teacher who closes it is not reopened on the next refresh.
  const touched = useRef(false);
  const [syncing, setSyncing] = useState(false);
  // ⚠️ THE DRAFT IS SEEDED FROM THE LOAD AND RE-SEEDED WHEN IT CHANGES IDENTITY. `useAsync`
  // refreshes on its own, and a draft rebuilt on every poll would discard whatever somebody
  // was in the middle of typing.
  const [qDraft, setQDraft] = useState<ProjectQuestion[] | null>(null);
  // 🔴 ONE DRAFT AND ONE SAVE, BECAUSE TWO WAS DATA LOSS. [X] `Save questions` sent
  // `{...loaded.form, questions}` — the settings the SERVER last returned — so a closing date
  // typed and not yet saved was silently written back to its old value, with no error and the
  // box still showing what you typed.
  const [fDraft, setFDraft] = useState<ProjectFormConfig | null>(null);
  const [busyQ, setBusyQ] = useState(false);
  const [busyOpen, setBusyOpen] = useState(false);
  const [qErr, setQErr] = useState<ApiError | null>(null);
  const [wrote, setWrote] = useState<string[]>([]);
  const loaded = overview.data;
  // 🔴 THE PLAN IS OPEN WHEN THE TAB IS (owner, 2026-09-23): *"when we open teams also appear
  // the curretn team!"*. Landing on a table of one row and having to press Review to see the
  // teams put the page's whole subject one click away. The published plan wins, otherwise the
  // newest — and `touched` means a teacher who closes it is not reopened by the next refresh.
  const currentId = currentOf(loaded?.proposals ?? [])?.id ?? "";
  useEffect(() => {
    if (!touched.current && currentId) setOpenProposal(currentId);
  }, [currentId]);
  const questions = qDraft ?? loaded?.student_form.questions ?? [];
  // 🔴 `d.form.questions` IS A TYPE LIE. `ProjectFormConfig` declares it required and
  // `_form_payload` has never sent it; the live source is `student_form.questions`.
  const form = fDraft ?? loaded?.form ?? null;
  const canEdit = access.can_manage_teams;

  // 🔴 `form` WAS A TAB AND IS NOW THREE. That address is in bookmarks and pasted into
  // Discord, so it redirects rather than falling through to a default — which would land
  // somebody on a different stage with nothing saying so.
  if (rawTab === "form") {
    return <Navigate to={routes.teams(courseId, "questions")} replace />;
  }
  const tab: TeamTab = isTeamTab(rawTab ?? "") ? (rawTab as TeamTab) : "questions";

  const changes = loaded && form
    ? countChanges(loaded.form, loaded.student_form.questions, form, questions)
    : { questions: 0, settings: 0, grouping: 0, total: 0 };
  const dirty = changes.total > 0;

  const saveForm = async () => {
    if (!loaded || !form) return;
    setBusyQ(true); setQErr(null); setWrote([]);
    try {
      // ⚠️ `enabled` COMES FROM THE SERVER, NOT THE DRAFT. The open/close switch writes
      // immediately and is deliberately not part of this save; carrying a stale copy of it
      // here would let a save silently re-close a form somebody had just opened.
      const r = await putProjectForm(courseId, { ...form, enabled: loaded.form.enabled,
                                                 questions });
      setQDraft(null); setFDraft(null);
      setWrote(r.changed ?? []);
      overview.reload();
    } catch (e) { setQErr(e as ApiError); }
    finally { setBusyQ(false); }
  };

  // 🔴 NOT PART OF THE DRAFT, AND THAT IS THE WHOLE POINT. This is the one control on the page
  // that changes what students see the moment it is pressed. [X] It was pulled onto the shared
  // draft once and wrote nothing until Save, while its own hint promised the opposite and the
  // pill beside it went on saying `off` — a teacher believing the form was open and waiting
  // for answers that could not arrive.
  const toggleOpen = () => {
    if (!loaded) return;
    setBusyOpen(true); setQErr(null);
    tracked(putProjectForm(courseId, { ...loaded.form, enabled: !loaded.form.enabled }))
      .then(() => overview.reload())
      .catch((e) => setQErr(e as ApiError))
      .finally(() => setBusyOpen(false));
  };

  const sync = () => {
    setSyncing(true);
    tracked(syncRoster(courseId)).finally(() => { setSyncing(false); overview.reload(); });
  };

  const href = (x: TeamTab) => routes.teams(courseId, x);
  const LABEL: Record<TeamTab, string> = {
    questions: "Questions", settings: "Settings", grouping: "Grouping rules",
    answers: "Answers", teams: "Teams",
  };
  const DIRTY: Partial<Record<TeamTab, number>> = {
    questions: changes.questions, settings: changes.settings, grouping: changes.grouping,
  };

  return (
    <div className="stack">
      <div className="page-head">
        <p className="eyebrow">Cohort</p>
        <h2>Teams</h2>
        {loaded && <span className="cohort-tag">{loaded.course.name}</span>}
        <span className="fresh"><Freshness q={overview} /></span>
      </div>

      <Pane q={overview} empty={<Empty title="Nothing to show for this cohort yet" />}>
        {(d) => (
          <div className="stack">
            {/* 🔴 THE ALARM IS ABOVE THE TABS, because it is true of every one of them. A form
                open over a roster nothing can match refuses every student who opens it, and
                putting that inside a tab hides it from the two stages where somebody is most
                likely to be waiting for replies that were all refused. */}
            <RosterAlarm d={d} onSync={sync} syncing={syncing} />

            <div className="glance">
              <Link to={href("settings")}><span className="k">Closes</span>
                <span className="v">
                  {d.form.closes_at
                    ? <When iso={d.form.closes_at} />
                    : <span className="muted">No deadline</span>}
                </span></Link>
              <Link to={href("answers")}><span className="k">Answered</span>
                <span className="v">
                  {d.summary.received}<span className="muted"> of {d.summary.enrolled}</span>
                </span></Link>
              <Link to={href("teams")}><span className="k">Plan</span>
                <span className="v">
                  {/* ⚠️ PUBLISHED IS THE ONE THAT REACHED STUDENTS. A cohort can carry
                      several proposals and at most one of them is what people were shown. */}
                  {d.published_proposal_id
                    ? <Badge tone="ok">published</Badge>
                    : d.proposals.length > 0
                      ? <span className="muted">{d.proposals.length} proposed</span>
                      : <span className="muted">none yet</span>}
                </span></Link>
              <div className="glance-open">
                <span className="k">Open to students</span>
                <span className={d.state === "open" ? "statepill on" : "statepill"}>
                  <span className="statedot" />{d.state}
                </span>
                <button className="btn sm" disabled={!canEdit || busyOpen}
                        onClick={toggleOpen}>
                  {busyOpen ? "…" : d.state === "open" ? "Close it" : "Open it"}
                </button>
                <FormLinks courseId={courseId} open={d.state === "open"} />
              </div>
            </div>

            <div className="ptabs" role="tablist">
              {TEAM_TABS.map((x) => (
                <Link key={x} className="ptab" role="tab" aria-selected={tab === x}
                      to={href(x)}>
                  {LABEL[x]}
                  {x === "questions" && (
                    <span className="nav-count">{questions.length}</span>)}
                  {x === "answers" && d.summary.received > 0 && (
                    <span className="nav-count">{d.summary.received}</span>)}
                  {/* ⚠️ A TEXT ALTERNATIVE, NOT A `title`. The dot is the answer to "one save
                      bar, three tabs" — it says where the unsaved edits are. */}
                  {(DIRTY[x] ?? 0) > 0 && (
                    <span className="unsaved" aria-label="unsaved changes" role="img" />)}
                </Link>))}
            </div>

            {qErr && <p className="alert refused" role="alert">⛔ {qErr.message}</p>}
            {wrote.length > 0 && !dirty && (
              <p className="alert info" role="status">Saved: {wrote.join(", ")}</p>)}

            {tab === "questions" && form && (
              <Card>
                <CardHead title="The questions"
                          desc={<>What this cohort asks. A new form starts as the programme's
                                  seven; add, reword, reorder or remove them.</>} />
                <CardBody>
                  <QuestionEditor questions={questions} disabled={!canEdit}
                                  warnings={d.identity_warnings}
                                  words={d.identity_words ?? []}
                                  onChange={setQDraft} />
                </CardBody>
              </Card>)}

            {tab === "settings" && form && (
              <SettingsTab form={form} loaded={d.form} d={d} canEdit={canEdit}
                           onChange={setFDraft} />)}

            {tab === "grouping" && form && (
              <GroupingRules form={form} loaded={d.form} d={d} questions={questions}
                             canEdit={canEdit} words={d.identity_words ?? []}
                             warnings={d.identity_warnings}
                             onQuestions={setQDraft} onForm={setFDraft} />)}

            {tab === "answers" && (
              <Intents d={d} courseId={courseId} cohortName={d.course.name}
                       canEdit={access.can_manage_teams}
                       onChanged={overview.reload} questions={d.student_form.questions} />)}

            {tab === "teams" && (
              <Proposals d={d} courseId={courseId} access={access}
                         open={openProposal}
                         setOpen={(v: string) => { touched.current = true; setOpenProposal(v); }}
                         onChanged={overview.reload} />)}

            {/* 🔴 ONE SAVE BAR FOR THREE TABS. Two buttons on one endpoint was not a layout
                preference — whichever you pressed last decided, and the other's edits went
                silently. The copy names where the changes are, so the count is verifiable. */}
            {dirty && canEdit && (
              <div className="savebar" role="status">
                <span><b>{changes.total} unsaved change{changes.total === 1 ? "" : "s"}</b>{" "}
                  <span className="muted">
                    · {whereChanged(changes, tab)}. One save writes the form, its questions
                    and these rules together.
                  </span>
                </span>
                <span className="spacer" />
                <button className="btn ghost" disabled={busyQ}
                        onClick={() => { setQDraft(null); setFDraft(null); }}>
                  Discard</button>
                <button className="btn primary" disabled={busyQ}
                        onClick={() => void saveForm()}>
                  {busyQ ? "Saving…" : `Save ${changes.total} change${changes.total === 1 ? "" : "s"}`}
                </button>
              </div>)}
          </div>
        )}
      </Pane>
    </div>
  );
}

/**
 * The three ways out of this page to the form itself, beside the switch that opens it.
 *
 * 🔴 THE STUDENT LINK AND THE PREVIEW ARE DIFFERENT THINGS AND THIS SAYS SO. [X]
 * `/apply/<id>/project` resolves an APPLICANT: a teacher is not on the student roster, so it
 * answers them "the Google account you signed in with is not on this cohort's Classroom
 * roster" — which is exactly what the owner was shown once already. It is still worth having,
 * because it is the address that gets pasted into Discord and somebody has to be able to reach
 * it; what it must not be is the thing labelled "see the form". The tooltips carry that
 * difference, because the icons cannot.
 *
 * ⚠️ `↗` ONLY WHILE THE ROUND IS OPEN. A shut form refuses every student who opens it, so a
 * control offering to go there would be offering a refusal. The PREVIEW is the one that works
 * either way — which is the whole reason it exists.
 */
/** The Teams form's three links. */
function FormLinks({ courseId, open }: { courseId: string; open: boolean }) {
  // 🔴 `/project`, NOT THE BARE PATH. [X] The bare path shows whichever rounds are open, so
  // with this form off it renders the INTAKE questions and nothing else.
  return (
    <LinkTrio open={open} who="student"
              url={`${window.location.origin}/apply/${encodeURIComponent(courseId)}/project`}
              preview={routes.teamsPreview(courseId)}
              openTitle="Open the link students use. You are not on the student roster, so it will refuse you unless you are — the preview is the one that shows the form." />
  );
}

/**
 * Open · preview · copy, for either form. ⚠️ ONE COMPONENT, so the Applicants strip and the
 * Teams strip cannot drift into two looks for the same three acts (2026-10-13).
 */
export function LinkTrio({ url, preview, open, who, openTitle }: {
  url: string; preview: string; open: boolean;
  /** Who the link is for, in the accessible names: "student" or "applicant". */
  who: string;
  openTitle: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="row gap">
      {open && (
        <a className="btn sm icon" href={url} target="_blank" rel="noreferrer"
           title={openTitle}
           aria-label={`Open the ${who} link in a new tab`}>
          <Glyph d={OUT} />
        </a>)}
      <Link className="btn sm icon" target="_blank" rel="noreferrer"
            to={preview}
            title={`See the form as ${who === "applicant" ? "an applicant" : `a ${who}`} does, drawn with their own controls. It shows the questions as SAVED, so an unsaved edit is not in it.`}
            aria-label={`Preview the form as ${who === "applicant" ? "an applicant" : `a ${who}`} sees it`}>
        <Glyph d={EYE} />
      </Link>
      <button className={copied ? "btn sm icon on" : "btn sm icon"} title={url}
              aria-label={`Copy the ${who} link, ${url}`}
              onClick={() => {
                void navigator.clipboard?.writeText(url);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              }}>
        <Glyph d={copied ? TICK : COPY} />
      </button>
    </span>
  );
}

/**
 * 🔴 DRAWN, NOT TYPED, AND THAT IS A DEPARTURE WORTH STATING. This design's rule is that
 * icons are text glyphs — the drag handle is a grid of dots, the arrows and `×` are characters
 * — and it holds while a glyph exists for the thing. There is no reliable one for an eye or
 * for "copy": `👁` is an EMOJI and renders in full colour on most platforms, which would put
 * the only coloured thing on the page inside a monochrome button. And a group of three
 * controls has to match optically, so mixing one text arrow with two drawings would be worse
 * than drawing all three.
 * ⚠️ `currentColor` AND NO FILL, so they inherit the button's ink in both themes and in the
 * `on` state rather than carrying a colour of their own.
 */
const OUT = "M14 3h7v7 M21 3 11 13 M21 14v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6";
const EYE = "M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z";
const COPY = "M9 9h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1Z M5 15H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v1";
const TICK = "M4 12.5 9.5 18 20 6";

function Glyph({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         focusable="false">
      {d.split(" M").map((seg, i) => <path key={i} d={i === 0 ? seg : `M${seg}`} />)}
    </svg>
  );
}

/**
 * 🔴 STOP, NOT A HINT. An open form over a roster with no names refuses everybody who opens
 * it, by design — the match is exact on the Google account and there is nothing to match
 * against. Rendering that as a quiet note beside a zero is how a teacher waits a week for
 * replies that were all refused.
 */
function RosterAlarm({ d, onSync, syncing }: {
  d: ProjectsOverview; onSync: () => void; syncing: boolean;
}) {
  const r = d.summary.roster;
  if (r.names) return null;
  return (
    <p className={d.state === "open" ? "alert refused" : "alert info"} role="alert">
      {d.state === "open" ? "⛔ " : "⚠️ "}
      {d.state === "open"
        ? "This form is open and nothing here can recognise a student. Every one who opens "
          + "it will be refused. "
        : "The roster has no names yet, so nobody could answer this form. "}
      {r.note}{" "}
      <button className="btn ghost sm" disabled={syncing} onClick={onSync}>
        {syncing ? "Syncing…" : "Sync roster"}
      </button>
    </p>
  );
}

/**
 * The four readings, INSIDE the card they describe (owner, 2026-09-09).
 *
 * 🔴 THEY WERE THEIR OWN CARD ABOVE THE ANSWERS, and the two blocks were one subject: how many
 * answered, how many a run would use, how many are still to come, how many are held out — every
 * one of them a count OF the list underneath. Two bordered boxes for one thing put a rule
 * between a number and the rows it counts, and pushed the list itself below the fold.
 */
function Summary({ d }: { d: ProjectsOverview }) {
  const s = d.summary;
  return (
    <div className="grid k4 kpi-strip">
      <Kpi label="Answered" value={s.received} />
      <Kpi label="Eligible" value={s.live}
           hint="What a grouping run would be built from." />
      {/* ⚠️ OUTSTANDING IS ENROLLED MINUS LIVE, and it is the number a teacher chases.
          It is not "missing": somebody may have withdrawn deliberately. */}
      <Kpi label="Yet to answer" value={s.outstanding}
           hint={`${s.enrolled} enrolled on this cohort`} />
      <Kpi label={s.tests ? "Set aside" : "Set aside"} value={s.excluded}
           hint={s.withdrawn ? `${s.withdrawn} withdrew` : undefined} />
      {/* ⚠️ ONLY WHERE THERE IS ONE. A permanent "Tests 0" on every cohort is a column of
          nothing that teaches the eye to skip the strip. */}
      {s.tests > 0 && (
        <Kpi label="Test submissions" value={s.tests}
             hint="Not counted above, and never grouped." />)}
    </div>
  );
}

/**
 * The four settings that govern the round — formerly a 296px rail.
 *
 * 🔴 THE RAIL COULD NOT HOLD ITS OWN CONTENT. Two of the fields in it were textareas whose
 * contents a model acts on, and they were the narrowest boxes on a 2500px page. Label left,
 * control right, capped at 760px.
 *
 * ⚠️ AN EDITED FIELD SAYS SO, AND THAT IS WHAT MAKES ONE SAVE BAR ACROSS THREE TABS
 * TRUSTWORTHY. The bar counts changes it cannot show you; the field marks its own.
 */
function SettingsTab({ form, loaded, d, canEdit, onChange }: {
  form: ProjectFormConfig; loaded: ProjectFormConfig; d: ProjectsOverview;
  canEdit: boolean; onChange: (f: ProjectFormConfig) => void;
}) {
  const set = <K extends keyof ProjectFormConfig>(k: K, v: ProjectFormConfig[K]) =>
    onChange({ ...form, [k]: v });
  const s = d.summary;
  // Arithmetic only, from figures already on the page.
  const teams = s.enrolled > 0 && form.team_size_max > 0
    ? Math.ceil(s.enrolled / form.team_size_max) : 0;
  const edited = (k: keyof ProjectFormConfig) => form[k] !== loaded[k];

  return (
    <div className="stack t-settings">
      <div>
        <h3 className="sec-title">How the round runs</h3>
        <p className="sec-desc">
          Four settings. Saving any of them does not open the form — the switch above does
          that. What the grouping model reads lives in Grouping rules.
        </p>
      </div>
      <Card>
        <CardBody>
          <div className="stack">
            <SetField label="Closes" edited={edited("closes_at")}
                      hint="Empty means no deadline. UTC unless you say otherwise.">
              <Deadline value={form.closes_at} disabled={!canEdit}
                        onChange={(v) => set("closes_at", v)} />
            </SetField>
            <SetField label="Team size"
                      edited={edited("team_size_min") || edited("team_size_max")}
                      hint="Between 1 and 6. One undersized team is arithmetic.">
              <span className="row gap">
                <input className="input" type="number" min={1} max={6} disabled={!canEdit}
                       aria-label="Smallest team" value={form.team_size_min}
                       onChange={(e) => set("team_size_min", Number(e.target.value))} />
                <span className="small muted">to</span>
                <input className="input" type="number" min={1} max={6} disabled={!canEdit}
                       aria-label="Largest team" value={form.team_size_max}
                       onChange={(e) => set("team_size_max", Number(e.target.value))} />
              </span>
              {teams > 0 && (
                <p className="small muted" style={{ margin: "6px 0 0" }}>
                  {s.enrolled} enrolled makes {teams} team{teams === 1 ? "" : "s"}.
                </p>)}
            </SetField>
            <SetField label="Intro" edited={edited("intro")}
                      hint="What students read above the questions.">
              <textarea className="ta" rows={3} value={form.intro} disabled={!canEdit}
                        onChange={(e) => set("intro", e.target.value)} />
            </SetField>
            {/* 🔴 "LINK FOR STUDENTS" HAS GONE FROM THIS TAB (owner, 2026-09-09). The
                handoff lists it as one of four settings; the link's three actions — go there,
                preview, copy — now sit beside the open/close switch in the glance strip, which
                is chrome and therefore visible from all five tabs rather than buried in one.
                Two Previews on one page was the immediate result of adding them there and
                leaving this, and the suite caught it: `Found multiple elements with the role
                "link" and name /Preview/`. The URL itself is on the Copy control's `title`. */}
            {!canEdit && <span className="small muted">
              You can read this. Changing it needs <b>Applicants &amp; teams</b> on this
              cohort.</span>}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export function SetField({ label, hint, edited = false, children }: {
  label: string; hint?: string; edited?: boolean; children: React.ReactNode;
}) {
  return (
    <div className={`setfield${edited ? " on" : ""}`}>
      <div className="lab">
        {label}
        {hint && <p>{hint}</p>}
      </div>
      <div>
        {children}
        {edited && <p className="edited" style={{ margin: "6px 0 0" }}>EDITED · NOT SAVED YET</p>}
      </div>
    </div>
  );
}

/**
 * What the grouping reads, what you ask of it, and what it does regardless.
 *
 * 🔴 THIS WAS A 296px TEXTAREA LABELLED "Guidance for the grouping". Everything the model
 * sees now says so on one page, in the order that matters: what it may read → what you ask of
 * it → what it cannot be asked to change.
 *
 * 🔴 THE THIRD CARD IS A PROMISE ON THE SERVICE'S BEHALF, AND EVERY ROW WAS VERIFIED IN
 * SOURCE BEFORE SHIPPING (2026-09-09, `api/turbine/domain/teams.py`). A wrong promise here is
 * worse than no list. Where a rule could not be confirmed the instruction was to delete the
 * row; all five were confirmed, and each cites where.
 */
/**
 * The prompt the job actually sends, with the teacher's paragraph marked where it lands.
 *
 * 🔴 THE PAGE USED TO DESCRIBE THIS INSTEAD OF SHOWING IT. "Read after the fixed rules. It can
 * express a preference; it cannot lift a rule." is a true sentence about a document a teacher
 * had never seen — so tuning guidance meant writing into the dark, and the one property the
 * sentence is about (that the rules come first) was taken on trust.
 *
 * 🔴 THE FIXED HALVES ARE NOT EDITABLE HERE, AND THAT IS THE DESIGN RATHER THAN AN OMISSION.
 * They live in the job, on the far side of the boundary this console cannot write to, which is
 * what makes "guidance cannot lift a rule" a fact about the system instead of a promise about
 * this page. Editing them is a commit to `scripts-teams/propose.py` and a deploy.
 *
 * ⚠️ IT IS COLLAPSED BY DEFAULT. It is the longest thing on the page and it changes only when
 * somebody changes the guidance above it; open on demand, closed by default.
 */
function PromptText({ prompt, guidance }: { prompt: PromptPreview; guidance: string }) {
  // ⚠️ THE LIVE DRAFT, NOT THE SAVED VALUE. A teacher typing guidance should see where it
  // lands as they type — the saved copy would show them the previous run's prompt.
  const shown = guidance || prompt.guidance;
  return (
    <details className="promptbox">
      <summary>
        The prompt this sends <span className="small muted">
          — {prompt.full.length.toLocaleString()} characters, one per student</span>
      </summary>
      <pre className="prompt"><code>
        <span className="prompt-fixed">{prompt.before}</span>
        <mark className="prompt-yours">{shown}</mark>
        <span className="prompt-fixed">{prompt.after}</span>
      </code></pre>
      <p className="small muted">
        The highlighted paragraph is yours. Everything around it is fixed in the job and
        cannot be changed from this page — which is what makes guidance unable to lift a
        rule rather than merely asked not to.
      </p>
    </details>
  );
}

function GroupingRules({ form, loaded, d, questions, canEdit, words, warnings,
                         onQuestions, onForm }: {
  form: ProjectFormConfig; loaded: ProjectFormConfig; d: ProjectsOverview;
  questions: ProjectQuestion[]; canEdit: boolean; words: string[];
  warnings: { id: string; label: string; word: string }[];
  onQuestions: (qs: ProjectQuestion[]) => void;
  onForm: (f: ProjectFormConfig) => void;
}) {
  const on = questions.filter((q) => q.grouping !== false).length;
  // The label of whichever question carries each rule, or "" when none does.
  const axes = {
    domain: questions.find((q) => q.axis === "domain")?.label ?? "",
    seniority: questions.find((q) => q.axis === "seniority")?.label ?? "",
  };
  const mates = questions.find((q) => q.kind === "teammates");
  const warned = new Set(warnings.map((w) => w.id));
  const reads = (q: ProjectQuestion) =>
    q.grouping !== false && words.some((w) => `${q.label} ${q.hint}`.toLowerCase().includes(w));
  const identity = (q: ProjectQuestion) => (q.id ? warned.has(q.id) : reads(q));
  const toggle = (i: number, v: boolean) =>
    onQuestions(questions.map((q, n) => (n === i ? { ...q, grouping: v } : q)));

  return (
    <div className="stack t-grouping">
      <div>
        <h3 className="sec-title">What the grouping reads</h3>
        <p className="sec-desc">
          A run reads the answers you allow, obeys the rules below, and proposes a plan.
          Nothing reaches students until you publish it in Teams.
        </p>
      </div>

      <Card>
        <CardHead title="Answers it may read"
                  desc={<>The same switch as <em>The grouping reads this</em> in a question's
                          drawer — one field, two places to reach it.</>}
                  right={<span className="kick">{on} of {questions.length} on</span>} />
        {/* 🔴 ONE FIELD, TWO SURFACES. This switch and the drawer's write the same
            `questions[i].grouping` on the same draft. Here it reads as a policy over the whole
            form, there as a detail of one question. Do not fork the state. */}
        {questions.map((q, i) => {
          const isOn = q.grouping !== false;
          const flag = identity(q);
          return (
            <label key={q.id || `new-${i}`}
                   className={`switchrow${isOn ? "" : " off"}${flag ? " flagged" : ""}`}>
              <span className="n">{i + 1}</span>
              <span>
                <span className="q">{q.label || "Untitled question"}</span>
                {flag && <> <span className="qchip warn">identity</span></>}
                <span className="why">
                  {flag
                    ? "This one names people. Leaving it on means the model sees who it is placing."
                    : isOn
                      ? "The model reads this answer when it proposes teams."
                      : "Off: the answer reaches no model, never makes a published plan read "
                        + "as stale, and stays editable after teams are out."}
                </span>
              </span>
              <span className={`st ${isOn ? "reads" : "hidden"}`}>
                {isOn ? "READS" : "HIDDEN"}
                <input type="checkbox" checked={isOn} disabled={!canEdit}
                       onChange={(e) => toggle(i, e.target.checked)} />
                <span className={`sw${isOn ? " on" : ""}`} aria-hidden="true"><i /></span>
              </span>
            </label>);
        })}
      </Card>

      {/* 🔴 THE GROUPING MODEL WAS A CONSTANT WHILE GRADING'S WAS A SETTING, AND THAT WAS
          NOT A DECISION. Its reading feeds the arrangement, so which model reads is a choice
          a teacher can make, with the models the gateway does not serve disabled in place. */}
      <Card>
        <CardHead title="Which model reads the answers"
                  desc={<>It reads one person at a time and returns a level and some skill
                          tags. The teams themselves are arithmetic and no model touches
                          them.</>} />
        <CardBody>
          <div className="optlist">
            {d.models.map((m) => {
              const on = form.model === m.id || (!form.model && m.id === d.models[0]?.id);
              return (
                <label key={m.id}
                       className={`opt${on ? " on" : ""}${m.unavailable ? " off" : ""}`}>
                  <input type="radio" name="grouping-model" value={m.id} checked={on}
                         disabled={!canEdit || !!m.unavailable}
                         onChange={() => onForm({ ...form, model: m.id })} />
                  <span className="t">{m.label}</span>
                  <span className="n">{m.note}</span>
                  {/* 🔴 THE REASON IS ON THE PAGE, NOT IN A TOOLTIP. You cannot hover a
                      control you are being stopped from using, and "greyed out with no
                      explanation" is indistinguishable from "broken". */}
                  {m.unavailable && <span className="x">{m.unavailable}</span>}
                </label>);
            })}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Your guidance"
                  desc={<>Read after the fixed rules. It can express a preference; it cannot
                          lift a rule.</>} />
        <CardBody>
          <textarea className="ta" rows={4} value={form.guidance} disabled={!canEdit}
                    aria-label="Your guidance"
                    onChange={(e) => onForm({ ...form, guidance: e.target.value })} />
          <div className="row gap" style={{ marginTop: 10 }}>
            {/* Informational, not a limit — the API enforces no maximum here. */}
            <span className="kick">
              Plain sentences read best · {form.guidance.length} characters
              {form.guidance !== loaded.guidance && " · edited"}
            </span>
            <span className="spacer" />
            <button className="btn ghost sm" disabled={!canEdit || !form.guidance}
                    onClick={() => onForm({ ...form, guidance: "" })}>Clear</button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="What it sends"
                  desc={<>Exactly the text the grouping job builds for one student, with your
                          guidance where it lands.</>} />
        <CardBody>
          <PromptText prompt={d.prompt} guidance={form.guidance} />
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Rules it cannot change"
                  desc={<>Fixed in the service, not on this page. Listed so guidance is
                          written against them rather than into them.</>} />
        <div className="grule">
          <span className="k">TEAM SIZE</span>
          <span className="t">
            Every team lands between your minimum and maximum — <b>{form.team_size_min}</b> and{" "}
            <b>{form.team_size_max}</b>. One undersized team is arithmetic, not a failure.{" "}
            <Link to={routes.teams(d.course.id, "settings")}>Change in Settings</Link>
          </span>
        </div>
        <div className="grule">
          <span className="k">ONE TEAM EACH</span>
          <span className="t">
            Nobody is placed twice, and nobody who answered is left out of the proposal.
          </span>
        </div>
        {/* 🔴 THE TWO RULES THAT DEPEND ON THIS FORM, AND THEY SAY WHICH STATE THEY ARE IN.
            A rule the arrangement enforces and a rule nothing carries read identically from
            the outside, and stating the first while the second is true is the shape of a
            promise with no mechanism. Each links to where it is set. */}
        <div className="grule">
          <span className="k">SAME DOMAIN</span>
          <span className="t">
            {axes.domain ? <>
              Split on <b>{axes.domain}</b>. A team mixes domains only when two classmates
              picked each other, or somebody chose a domain nobody else did — and the plan
              names which.
            </> : <>
              <b>Not set.</b> No question is marked as the domain, so this is not enforced —
              mark one on{" "}
              <Link to={routes.teams(d.course.id, "questions")}>Questions</Link>.
            </>}
          </span>
        </div>
        <div className="grule">
          <span className="k">PICKED TEAMMATES</span>
          <span className="t">
            {mates ? <>
              From <b>{mates.label}</b>, up to {mates.max_picks ?? 2} each. Two classmates who
              picked each other are always on one team — even across domains, building in the
              domain most of them chose. Then as many people as possible get at least one of
              their picks, and only then are experience, skills and hours weighed. Nobody is
              told who picked them.
            </> : <>
              <b>Not set.</b> Add a question of the kind &ldquo;Classmates to team up with&rdquo;
              on <Link to={routes.teams(d.course.id, "questions")}>Questions</Link> to let
              students pick who they want to work with.
            </>}
          </span>
        </div>
        <div className="grule">
          <span className="k">MIXED EXPERIENCE</span>
          <span className="t">
            {axes.seniority ? <>
              Read from <b>{axes.seniority}</b>, in the order its choices are listed. Teams
              are built strongest-with-newest — as far as the cohort allows, which is at most
              as many teams as there are people on the lowest band.
            </> : <>
              <b>Not set.</b> No question is marked as experience, so this is not enforced —
              mark one on{" "}
              <Link to={routes.teams(d.course.id, "questions")}>Questions</Link>.
            </>}
          </span>
        </div>
        <div className="grule">
          <span className="k">HOURS OVERLAP</span>
          <span className="t">
            Preferred, never guaranteed. Read from the time-zone question; somebody who typed
            a zone the console cannot read has no hours, and the team says so rather than
            guessing they are in London.
          </span>
        </div>
        <div className="grule">
          <span className="k">COMPLEMENTARY SKILLS</span>
          <span className="t">
            Preferred, never guaranteed: it is read from what the model makes of the free-text
            answers, so within one domain there may be nobody to complement. Every team says
            whether it was met.
          </span>
        </div>
        <div className="grule">
          <span className="k">NO ANSWER, NO PLACE</span>
          <span className="t">
            A student who has not answered is not grouped.{" "}
            <Link to={routes.teams(d.course.id, "answers")}>Chase them in Answers</Link>
          </span>
        </div>
        <div className="grule">
          <span className="k">NOTHING ELSE READ</span>
          <span className="t">
            Only the answers switched on above reach the model. Grades, applications and
            social activity do not.
          </span>
        </div>
        <div className="grule">
          <span className="k">IT PROPOSES ONLY</span>
          <span className="t">
            A run produces a plan you review. Students see nothing until you publish.
          </span>
        </div>
      </Card>

      {/* Shown when a run would place nobody. Rules still save. */}
      {d.summary.received === 0 && (
        <div className="alert warn" role="status">
          <span className="ico">⚠️</span>
          <div className="body">
            <div className="t">Not runnable yet</div>
            <div className="m" id="why-not-runnable">
              A run now would place nobody — 0 of {d.summary.enrolled} have answered.
              Rules save without running.
            </div>
          </div>
          {/* ⚠️ THE REASON IS ASSOCIATED, so it is not colour and position alone. */}
          <button className="btn sm" disabled aria-describedby="why-not-runnable">
            Propose teams</button>
        </div>)}
    </div>
  );
}

/**
 * Who has answered, what they said, and who has not.
 *
 * 🔴 ONE CARD PER STUDENT, NOT A COLUMN PER QUESTION. The column-per-question comment was
 * right while it was written — a fixed header would have rendered a cohort's own answers
 * under somebody else's headings — but since the questions became the teacher's there is no
 * ceiling on how many there are, and the answers are prose. Six free-text columns gives every
 * row a different height and pushes the action off the right edge. Label-and-value pairs in
 * the form's own order is what `Review` already does, so the two tabs stop disagreeing about
 * what an answer looks like.
 *
 * 🔴 "YET TO ANSWER" HAS NAMES BEHIND IT NOW. The server left-joins the roster, so somebody
 * who has not replied is a row with no answers rather than an absence — which is why it can
 * be a filter at all.
 */
/**
 * The Answers tab as a spreadsheet.
 *
 * 🔴 IT CARRIES EXACTLY WHAT THE TAB ALREADY RENDERS, and that is the whole privacy argument.
 * Name, address, the three state flags, when it was last touched, and the answers — every one
 * of them already on the teacher's screen a few lines below. `level`, the model's rationale
 * and a student's `google_sub` are one join away and are on neither the payload nor this file.
 * ⚠️ `excluded_reason` IS ON THE ROW AND IS DELIBERATELY NOT HERE. [X] It is rendered only on
 * the Applicants page, as a tooltip; this tab never shows it, and the rule above is what the
 * TAB renders rather than what the payload happens to carry. Add it here only by also showing
 * it here.
 *
 * ⚠️ IT EXPORTS WHAT IS SHOWN, NOT EVERYTHING. The filters are directly above the button, so a
 * teacher who has narrowed to "yet to answer" and presses Export means those people. It is
 * also the safer default: you cannot take out more than you are looking at, and the button
 * says how many.
 *
 * ⚠️ ONE STATUS COLUMN WITH A PRECEDENCE, because a spreadsheet filters one column far more
 * easily than it combines three booleans. Withdrawn beats set aside beats answered.
 */
export function answersSheet(rows: TeacherIntentRow[], questions: ProjectQuestion[]):
    { header: string[]; body: string[][] } {
  const header = ["Name", "Email", "Status", "Last updated",
                  ...questions.map((q) => q.label)];
  const body = rows.map((r) => [
    r.display_name,
    r.email,
    // 🔴 THE TEST STATE WINS, because a spreadsheet is where the marking is most easily lost.
    // On the page the chip sits beside the name; in a file with no chips, a test submission
    // reading "answered" is indistinguishable from a student's and gets counted as one.
    r.is_test ? "test submission"
      : r.withdrawn ? "withdrew"
        : r.excluded ? "set aside"
          : r.updated_at ? "answered" : "yet to answer",
    r.updated_at,
    // ⚠️ BY QUESTION, NOT BY WHAT THE ROW HAPPENS TO HOLD. An answer to a question since
    // deleted is not exported, and a question added since somebody answered gives an empty
    // cell rather than shifting every column after it.
    ...questions.map((q) => (q.kind === "teammates" ? pickedNames(r.picks)
                                                     : r.answers?.[q.id] ?? "")),
  ]);
  return { header, body };
}

/** Picks as one cell of text: names, and "(picked back)" where it was mutual. */
function pickedNames(picks: { name: string; mutual: boolean }[] | undefined): string {
  return (picks ?? []).map((x) => `${x.name}${x.mutual ? " (picked back)" : ""}`).join("; ");
}

/**
 * What a file would load, before it loads it.
 *
 * 🔴 THE COLUMNS ARE MATCHED BY LABEL AND THE DATABASE KEYS BY ID, so "which of my columns
 * landed nowhere" is the one question this has to answer. A file whose Domain column matched
 * nothing would import everybody with no domain, and the arrangement would put them all in one
 * bucket and look like it was working.
 *
 * ⚠️ THE REFUSAL ARRIVES AS DATA, not as an error — a preview writes nothing, so there is
 * nothing to signal with a status code.
 */
function ImportPanel({ d, busy, onCancel, onLoad }: {
  d: Record<string, unknown>; busy: boolean; onCancel: () => void;
  onLoad: (o: { replace: boolean; keep_identity: boolean }) => void;
}) {
  const [names, setNames] = useState(false);
  // 🔴 A REPLACE DELETES ROWS AND WAS ONE CLICK. The button read "Replace 44 and load 43" and
  // did exactly that on the first press — no confirmation anywhere, for the only operation in
  // this console that removes answers. Loading is safe and re-runnable; deleting forty-four
  // rows because a file happened to be dropped on the wrong cohort is not.
  // ⚠️ INLINE, NOT `window.confirm`. A browser dialog cannot show the two numbers that decide
  // it — how many go and how many arrive — and this console renders every other consequence
  // where the control is.
  const [confirming, setConfirming] = useState(false);
  const rows = Number(d.rows ?? 0);
  const unmatched = (d.unmatched as string[] | undefined) ?? [];
  const missing = (d.unanswered_questions as string[] | undefined) ?? [];
  const already = Number(d.imported_intents ?? 0);
  const enrolled = Number(d.real_roster ?? 0);
  const ok = d.would_run === true;
  // A teammates column holds names, matched against this file's own rows. A name that links
  // nobody is a pick that would silently vanish, so each kind of miss is listed by name.
  const picks = d.picks as { linked: number; unmatched: string[]; ambiguous: string[] }
    | undefined;

  return (
    <div className={`alert stack ${ok ? "info" : "refused"}`} role="status">
      <p style={{ margin: 0 }}>
        {ok
          ? <><strong>{rows}</strong> answered row(s) would load
              {Number(d.skipped) > 0 && <> · {String(d.skipped)} without answers skipped</>}
              {Number(d.no_zone) > 0 && <> · {String(d.no_zone)} typed a zone this console
                does not know, so they have no offset</>}
              {(picks?.linked ?? 0) > 0 && <> · {picks!.linked} teammate pick(s) link people in
                this file</>}.</>
          : <>{String(d.reason ?? "That file cannot be loaded here.")}</>}
      </p>
      {ok && enrolled > 0 && (
        // ⚠️ SAID, NOT LEFT TO BE DISCOVERED. A cohort with a synced roster is the normal
        // place to load a test file, and these people arrive as rows of their own — so the
        // list afterwards holds both, and nothing has been attributed to anybody real.
        <p className="small" style={{ marginTop: 6 }}>
          <strong>{enrolled}</strong> student(s) are already enrolled here and will stay.
          The loaded answers belong to new rows, not to them.
        </p>
      )}
      {unmatched.length > 0 && (
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Ignored columns:</strong> {unmatched.join(", ")} — nothing on this form is
          called that.
        </p>
      )}
      {missing.length > 0 && (
        <p className="small" style={{ marginTop: 6 }}>
          <strong>No data for:</strong> {missing.join(", ")} — those answers will be empty.
        </p>
      )}
      {(picks?.unmatched.length ?? 0) > 0 && (
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Picked, and nobody here has the name:</strong> {picks!.unmatched.join(", ")}
          {" "}— those picks are left out, because nobody this file loads is called that.
        </p>
      )}
      {(picks?.ambiguous.length ?? 0) > 0 && (
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Picked, and more than one row has the name:</strong>{" "}
          {picks!.ambiguous.join(", ")} — those picks are left out, because there is no telling
          which person was meant.
        </p>
      )}
      {/* 🔴 THE SENTENCE NAMES WHAT GOES, NOT ONLY WHAT ARRIVES. "Replace 44 and load 43" on
          a button is a description of an outcome; this is the moment to say that forty-four
          answers are deleted and that nothing else on the cohort is touched. */}
      {ok && confirming && (
        <p className="alert warn stack-tight" role="alert" style={{ margin: "10px 0 0" }}>
          <strong>Delete {already} imported answer(s) and load {rows}?</strong>{" "}
          The {already} already here were loaded from a file and go permanently. Nothing else
          on this cohort is touched — the roster stays, and no answer anybody actually typed
          is ever deleted by this.
        </p>
      )}
      {ok && (
        <div className="row gap wrap" style={{ marginTop: 10, alignItems: "center" }}>
          {already > 0 && !confirming ? (
            <button className="btn sm" disabled={busy} onClick={() => setConfirming(true)}>
              Replace {already} and load {rows}
            </button>
          ) : (
            <button className={`btn sm ${confirming ? "danger" : "primary"}`} disabled={busy}
                    onClick={() => onLoad({ replace: already > 0, keep_identity: names })}>
              {busy ? "Loading…"
                    : confirming ? `Delete ${already} and load ${rows}` : `Load ${rows}`}
            </button>
          )}
          <button className="btn ghost sm" disabled={busy}
                  onClick={() => (confirming ? setConfirming(false) : onCancel())}>
            {confirming ? "Keep them" : "Cancel"}
          </button>
          {/* 🔴 OFF BY DEFAULT, AND THE LABEL SAYS WHAT IT COSTS. The grouping reads no
              identity, so names buy nothing but recognising people in the review. */}
          <label className="small muted" style={{ display: "flex", gap: 6 }}>
            <input type="checkbox" checked={names} disabled={busy}
                   onChange={(e) => setNames(e.target.checked)} />
            Keep their names — the grouping never reads them
          </label>
        </div>
      )}
      {!ok && (
        <button className="btn ghost sm" style={{ marginTop: 8 }} onClick={onCancel}>
          Close
        </button>
      )}
    </div>
  );
}

/**
 * The published — or proposed — teams, as a spreadsheet.
 *
 * 🔴 ONE ROW PER PERSON, NOT ONE PER TEAM. A team per row needs a column per member and the
 * widest team decides how many, so a cohort of threes grows two empty columns the moment
 * somebody is moved. A row per person sorts, filters and pivots; it is also the shape somebody
 * pastes into a mail merge, which is what this file is for.
 *
 * 🔴 THE MODEL'S READING TRAVELS AND IS LABELLED AS THE MODEL'S. `level`, `skills` and its
 * sentence are on the teacher's review screen already, and a file a teacher takes away to
 * decide with should not be poorer than the screen they decided on. They are NOT on any
 * student-facing payload and this is not one — `team_view` builds that separately.
 *
 * ⚠️ THE RULES TRAVEL AS THE WORDS THE SCREEN SHOWS, one column each. A team that could not
 * have a mix of experience is a fact about who applied, and a spreadsheet that dropped it
 * would read as sixteen teams nobody checked.
 */
export function teamsSheet(p: ProposalDetail): { header: string[]; body: string[][] } {
  // ⚠️ DERIVED FROM THE LABELS, so a rule cannot reach the screen and miss the sheet. The
  // two used to be separate literals and would have drifted on the next rule added.
  const rules = Object.keys(LABELS) as TeamRule["rule"][];
  const asked = p.questions.filter((q) => q.kind !== "teammates");
  const header = [
    "Team", "Team name", "Size", "Name", "Email",
    ...asked.map((q) => q.label),
    "Picked",
    "Model: level", "Model: skills", "Model: why",
    ...rules.map((r) => `Rule: ${r}`),
  ];
  const body = p.teams.flatMap((t) => {
    const by = new Map(t.rules.map((r) => [r.rule, r]));
    return t.members.map((m) => [
      String(t.number),
      t.name,
      String(t.members.length),
      m.display_name,
      m.email,
      // ⚠️ BY QUESTION, NOT BY WHAT THE ROW HAPPENS TO HOLD — and by the PROPOSAL's questions,
      // so a form reworded since the run still reads under the labels it ran against.
      ...asked.map((q) => m.answers?.[q.id] ?? ""),
      // ⚠️ WHERE EACH PICK ENDED UP, in words — the question a teacher asks of this column.
      (m.picks ?? []).map((x) => `${x.name}${x.mutual ? " (picked back)" : ""}: ${
        x.team === t.number ? "same team" : x.team ? `team ${x.team}` : "not in this plan"}`)
        .join("; "),
      m.level,
      m.skills.join(", "),
      m.rationale,
      ...rules.map((r) => {
        const row = by.get(r);
        if (!row) return "";
        // The reason is what makes `not met` useful; `met` has none and needs none.
        return row.state === "met" ? "met" : `${row.state}: ${row.reason}`;
      }),
    ]);
  });
  return { header, body };
}

/**
 * One student's answers, open for a teacher to change (owner, 2026-09-22).
 *
 * 🔴 THE STUDENT'S OWN RENDERER AND CONTROLS — `ProjectQuestions` with `QCard`, `Chips` and
 * `ZoneChips`, as the form preview uses — so the editor offers exactly the choices the student
 * had, and the server's validator is the one that refuses.
 * 🔴 `locked` IS THE SERVER'S, and the save decides again. After publication the questions the
 * grouping read are fixed here with the reason; anything that still disagrees comes back as the
 * refusal, shown rather than swallowed.
 * ⚠️ THE WHOLE SET IS SENT, the way the student's save sends it. The server merges, so an answer
 * to a question since retired is kept.
 */
function AnswersEditor({ courseId, intentId, onClose, onSaved }: {
  courseId: string; intentId: string; onClose: () => void; onSaved: () => void;
}) {
  const q = useAsync(() => getIntentAnswers(courseId, intentId), [courseId, intentId]);
  const [draft, setDraft] = useState<ApplyProjectBody | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  const save = async (answers: ApplyProjectBody) => {
    setBusy(true); setErr(null);
    try { await editIntentAnswers(courseId, intentId, answers); onSaved(); }
    catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  };

  return (
    <div className="acard-body stack">
      <Pane q={q}>
        {(d: EditableAnswers) => {
          const current = draft ?? d.answers;
          const fixed = new Set(d.locked);
          return (
            <>
              <ProjectQuestions form={d.form} draft={current} classmates={d.classmates}
                                disabled={(qq) => busy || fixed.has(qq.id)}
                                note={(qq) => fixed.has(qq.id)
                                  ? "Teams are published, so this answer is fixed. Unpublish "
                                    + "the teams to change it."
                                  : ""}
                                set={(id, v) => setDraft({ ...current, [id]: v })}
                                Q={QCard} Pills={Chips} Other={ZoneChips} />
              {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
              <div className="row gap">
                <button className="btn primary" disabled={busy || draft === null}
                        onClick={() => void save(current)}>Save answers</button>
                <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
              </div>
            </>
          );
        }}
      </Pane>
    </div>
  );
}

function Intents({ d, courseId, cohortName, canEdit, onChanged, questions }: {
  d: ProjectsOverview; courseId: string; cohortName: string;
  // ⚠️ ONE PROP FOR EVERY WRITE ON THIS TAB. Setting somebody aside and loading a file had
  // two, because they borrowed two different grading permissions; both are Applicants &
  // teams now (2026-09-22), and a second prop carrying the same value is a place for the
  // two to drift apart again.
  canEdit: boolean;
  onChanged: () => void; questions: ProjectQuestion[];
}) {
  const q = useAsync(() => getProjectIntents(courseId), [courseId]);
  const [busy, setBusy] = useState("");
  const jsonLabel = "Download the answers for another console (no names)";
  const loadLabel = "Load answers from a file";
  const [imp, setImp] = useState<Record<string, unknown> | null>(null);
  const [file, setFile] = useState<string | null>(null);

  // ⚠️ THE FILE IS READ IN THE BROWSER AND THE TEXT IS WHAT TRAVELS. There is no upload
  // endpoint and no multipart handling: a sheet of answers is text, and a JSON body is the
  // one shape every other call here already uses.
  const preview = useCallback(async (f: File) => {
    setBusy("import"); setErr(null);
    try {
      const csv = await f.text();
      setFile(csv);
      setImp(await importAnswers(courseId, { csv }));
    } catch (e) { setErr(e as ApiError); }
    finally { setBusy(""); }
  }, [courseId]);

  const load = useCallback(async (o: { replace: boolean; keep_identity: boolean }) => {
    if (!file) return;
    setBusy("import"); setErr(null);
    try {
      await importAnswers(courseId, { csv: file, confirm: true, ...o });
      setImp(null); setFile(null);
      q.reload(); onChanged();
    } catch (e) { setErr(e as ApiError); }
    finally { setBusy(""); }
  }, [courseId, file, q, onChanged]);
  // ⚠️ FETCHED ON PRESS, NOT WITH THE PAGE. Nobody needs this on every load of the Answers
  // tab, and it is the one payload that reads every intent's answers in full.
  const exportJson = useCallback(async () => {
    const doc = await exportIntents(courseId);
    const blob = new Blob([JSON.stringify(doc, null, 2)],
                          { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = csv.stamped(cohortName, "intents").replace(/\.csv$/, ".json");
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [courseId, cohortName]);
  const [err, setErr] = useState<ApiError | null>(null);
  const [only, setOnly] = useState<"" | "quiet" | "aside">("");
  const [find, setFind] = useState("");
  // The one row whose answers are open for editing; "" when none is.
  const [editing, setEditing] = useState("");

  const toggle = useCallback(async (row: TeacherIntentRow) => {
    setBusy(row.id); setErr(null);
    try { await excludeIntent(courseId, row.id, !row.excluded); q.reload(); onChanged(); }
    catch (e) { setErr(e as ApiError); }
    finally { setBusy(""); }
  }, [courseId, q, onChanged]);

  const shown = (rows: TeacherIntentRow[]) => rows.filter((r) => {
    const answered = Boolean(r.updated_at);
    if (only === "quiet" && answered) return false;
    if (only === "aside" && !r.excluded && !r.withdrawn) return false;
    if (!find.trim()) return true;
    // ⚠️ NAME **OR** ANSWER TEXT. A teacher looking for "who said oracles" has no other route.
    const hay = [r.display_name, r.email, ...Object.values(r.answers ?? {})]
      .join(" ").toLowerCase();
    return hay.includes(find.trim().toLowerCase());
  });

  const n = q.data ? shown(q.data.rows).length : 0;
  const exportLabel = `Export ${n} answer${n === 1 ? "" : "s"} as CSV`;

  return (
    <Card>
      <CardHead title="Answers" desc={d.note}
                right={<Badge tone={d.state === "open" ? "ok" : "plain"}>{d.state}</Badge>} />
      <Summary d={d} />
      <div className="log-filters">
        <button className={only ? "chip" : "chip on"} aria-pressed={!only}
                onClick={() => setOnly("")}>All</button>
        <button className={only === "quiet" ? "chip on" : "chip"} aria-pressed={only === "quiet"}
                onClick={() => setOnly(only === "quiet" ? "" : "quiet")}>Yet to answer</button>
        <button className={only === "aside" ? "chip on" : "chip"} aria-pressed={only === "aside"}
                onClick={() => setOnly(only === "aside" ? "" : "aside")}>Set aside</button>
        {/* ⚠️ IT GROWS AND SHRINKS RATHER THAN WRAPPING. As `.input.wide` it held a fixed
            width, so the export was pushed onto a line of its own under the chips. */}
        <input className="input grow" value={find} placeholder="Find a name or an answer"
               aria-label="Find a name or an answer"
               onChange={(e) => setFind(e.target.value)} />
        {/* 🔴 THE COUNT IS IN THE ACCESSIBLE NAME, NOT ONLY IN THE GLYPH. The filters above
            decide what this takes, and a control that silently exported something other than
            what is on screen would be found out in a spreadsheet, later, by somebody counting.
            An icon cannot carry that sentence, so the label and the tooltip both do — which is
            also what makes an icon-only button usable at all. */}
        {/* ⚠️ NOT `ghost`. A ghost button is a transparent border and muted ink, which
            on a control whose whole content is one glyph reads as a stray character
            rather than something to press. It takes the ordinary button edge, like the
            filter chips it sits beside. */}
        <button className="btn sm icon"
                disabled={!q.data || shown(q.data.rows).length === 0}
                title={exportLabel} aria-label={exportLabel}
                onClick={() => {
                  const rows = shown(q.data?.rows ?? []);
                  const { header, body } = answersSheet(rows, questions);
                  csv.download(csv.stamped(cohortName, "answers"),
                               csv.toCsv(header, body));
                }}>
          <span aria-hidden="true">⤓</span>
        </button>
        {/* 🔴 A SECOND FILE, FOR A DIFFERENT READER. The CSV above is for a person opening a
            spreadsheet: names, addresses, one column per question. This one is for another
            console — the questions with their ids and axes, the answers keyed by those ids,
            and NO identity at all, because what makes a grouping worth testing is the shape
            of what people wrote and none of that needs a name. `tools/import_intents.py`
            loads it. */}
        <button className="btn sm icon" title={jsonLabel} aria-label={jsonLabel}
                onClick={() => { void exportJson(); }}>
          <span aria-hidden="true">{"{ }"}</span>
        </button>
        {canEdit && (
          <>
            {/* ⚠️ THE INPUT IS THE CONTROL AND THE BUTTON IS ITS LABEL. A bare file input
                cannot be styled to match the row it sits in, and hiding it behind a button
                that is not a <label> loses the keyboard path to it. */}
            <label className="btn sm icon" title={loadLabel}>
              <span aria-hidden="true">⤒</span>
              <span className="sr">{loadLabel}</span>
              <input type="file" accept=".csv,.json,text/csv,application/json"
                     className="sr" disabled={busy === "import"}
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       e.target.value = "";     // so the same file can be picked twice
                       if (f) void preview(f);
                     }} />
            </label>
          </>
        )}
      </div>
      <CardBody>
        {/* ⚠️ SAID ONCE FOR THE CARD. Set aside and Edit are both absent without the permission,
            and a missing control reads as a feature that does not exist unless something says
            which permission it is. */}
        {!canEdit && (
          <p className="small muted">
            Editing answers and setting people aside need <b>Applicants &amp; teams</b> on this
            cohort.
          </p>)}
        {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
        {imp && <ImportPanel d={imp} busy={busy === "import"}
                             onCancel={() => { setImp(null); setFile(null); }}
                             onLoad={(o) => { void load(o); }} />}
        <Pane q={q} empty={<Empty title="Nobody is enrolled on this cohort yet" />}>
          {(d) => {
            const rows = shown(d.rows);
            return rows.length === 0
              ? <Empty title="Nothing matches that" />
              : (
              <div className="stack-tight">
                {rows.map((r) => {
                  const answered = Boolean(r.updated_at);
                  return (
                    // ⚠️ THE ROSTER KEY, NOT THE NAME. A "yet to answer" row has `id: ""`,
                    // so two enrolled students who happen to share a display name produced
                    // duplicate React keys and one of them stopped updating.
                    <div key={r.id || `s:${r.student_id}`}
                         className={r.excluded || r.withdrawn ? "acard faint" : "acard"}>
                      <div className="acard-head">
                        <strong>{r.display_name}</strong>
                        {r.email && <span className="acard-mail">{r.email}</span>}
                        {/* 🔴 SAID, NOT IMPLIED. A test submission that looks like a
                            student's states something about this cohort that is not true —
                            and the person most likely to be misled is whoever sent it and
                            then forgot. It is out of every count on the strip above and out
                            of every grouping run; this is where it says so. */}
                        {r.is_test && <span className="tierchip" title="Sent to check the form
works, by a teacher or an address on the tester list. It is not counted among the students who
answered and it never reaches a grouping run.">test</span>}
                        {r.withdrawn && <span className="tierchip">withdrew</span>}
                        {r.excluded && <span className="tierchip">set aside</span>}
                        {/* ⚠️ THE STUDENT'S OWN PAGE SAYS THE SAME, dated. Who is on the Logs page. */}
                        {r.teacher_edited_at && (
                          <span className="tierchip" title={`Changed by a teacher on ${
                            new Date(r.teacher_edited_at).toLocaleString()}`}>
                            edited by a teacher</span>)}
                        {/* ⚠️ HOLLOW AND COLOURLESS, the same argument `index.css` makes about
                            HELD: a withdrawal is not a failing and neither is being held out
                            of a run. */}
                        {!answered && <span className="tierchip">yet to answer</span>}
                        <span className="spacer" />
                        {answered && <span className="small muted"><When iso={r.updated_at} /></span>}
                        {canEdit && answered && (
                          <button className="btn ghost sm" disabled={busy === r.id}
                                  onClick={() => void toggle(r)}>
                            {r.excluded ? "Put back" : "Set aside"}</button>)}
                        {canEdit && answered && (
                          <button className="btn ghost sm" disabled={busy === r.id}
                                  aria-label={editing === r.id
                                    ? `Close the editor for ${r.display_name}`
                                    : `Edit ${r.display_name}'s answers`}
                                  onClick={() => setEditing(editing === r.id ? "" : r.id)}>
                            {editing === r.id ? "Close" : "Edit"}</button>)}
                      </div>
                      {answered && editing === r.id && (
                        <AnswersEditor courseId={courseId} intentId={r.id}
                                       onClose={() => setEditing("")}
                                       onSaved={() => { setEditing(""); q.reload(); onChanged(); }} />
                      )}
                      {answered && editing !== r.id && (
                        <div className="acard-body">
                          {questions.map((qq) => {
                            const a = r.answers?.[qq.id] ?? "";
                            if (qq.kind === "teammates") {
                              return (
                                <div className="arow" key={qq.id}>
                                  <span className="alabel">{qq.label}</span>
                                  {r.picks.length === 0
                                    ? <span className="muted">{DASH}</span>
                                    : <PickNames picks={r.picks} />}
                                </div>);
                            }
                            return (
                              <div className="arow" key={qq.id}>
                                <span className="alabel">{qq.label}</span>
                                {!a ? <span className="muted">{DASH}</span>
                                  : qq.kind === "link"
                                    // ⚠️ THE ADDRESS, NOT THE WORD "Open". Seeing
                                    // `github.com/…` before you click is worth more than a
                                    // verb, and the stacked layout has the width for it.
                                    ? <a className="aval brk" href={a} target="_blank"
                                         rel="noreferrer noopener">{a}</a>
                                    : <span className={qq.kind === "timezone"
                                                       ? "aval mono" : "aval"}>{a}</span>}
                              </div>);
                          })}
                        </div>)}
                    </div>);
                })}
              </div>);
          }}
        </Pane>
      </CardBody>
    </Card>
  );
}


/**
 * Which grouping rules this team meets, and why not where it does not.
 *
 * 🔴 WORDS, NEVER A NUMBER. The schema deliberately carries no rank and no fit score
 * (`test_teams_privacy.py` names `fit`, `score` and `rank` among the columns that must not
 * exist), and a score on this card would be one step from ranking the teams — which is one
 * step from ranking the people in them. A teacher does not need to know that this team scored
 * 0.72; they need to know that everybody in it answered the same experience band.
 *
 * 🔴 `not measured` IS ITS OWN STATE AND IS NOT `not met`. "No question on this form is marked
 * as the domain" is something the teacher can fix in two clicks; "everyone here gave the same
 * answer" is a fact about who applied and nothing can fix it. Collapsing them would send
 * somebody looking for a defect that is not there — and would let a rule nothing enforces read
 * as one that was.
 *
 * 🔴 A MET RULE IS A TICK AND AN UNMET ONE IS A SENTENCE, AND THEY CANNOT SHARE A SHAPE.
 * [X] Seen in a render, not in the source: a sentence-long reason inside a pill wrapped its
 * own LABEL onto two lines and the chip read as broken. The met rules are the scannable half, which a teacher's eye passes
 * over; the unmet ones are the half they have to read, and reading wants full width.
 * ⚠️ AND THE NAME APPEARS ONCE. The previous version rendered every rule as a badge and then
 * repeated the failing ones underneath with the name again, so the one line that mattered
 * arrived twice and the card was longer for it.
 */
function TeamRules({ rules }: { rules: TeamRule[] }) {
  // ⚠️ `size` IS SHOWN ONLY WHEN IT IS NOT MET, AND IT IS THE ONE RULE THAT EARNS THAT. It is
  // met by construction on every team but the arithmetic leftover — `arrange.sizes` cannot
  // build a second short one — so a green chip for it would be five green words on thirteen
  // cards to flag the one that is different. [X] Seen in a render, not in the source.
  // 🔴 A FACT ABOUT THE FORM IS NOT A FACT ABOUT THIS TEAM. [X] A form-level "not
  // configured" sentence sat on every one of sixteen cards — the same for all of them, and
  // already on the Grouping rules tab where it can be acted on.
  // ⚠️ FILTERED ON `scope`, NEVER ON THE RULE NAME: scoping by name would hide the domain row
  // on every team that actually crosses one, which is the row that matters most.
  const shown = rules.filter((r) => r.scope !== "form")
    .filter((r) => r.rule !== "size" || r.state !== "met");
  const met = shown.filter((r) => r.state === "met");
  const missed = shown.filter((r) => r.state !== "met");
  return (
    <div className="trules">
      {met.length > 0 && (
        <div className="row wrap gap">
          {met.map((r) => (
            <Badge key={r.rule} tone="ok" dot>{LABELS[r.rule] ?? r.rule}</Badge>
          ))}
        </div>
      )}
      {missed.map((r) => (
        <p key={r.rule} className={`tmiss ${r.state === "not met" ? "no" : "un"}`}>
          {/* ⚠️ THE MARK IS PART OF THE NAME, not a separate element, so the two cannot wrap
              apart from each other — which is what made the old pill read as broken. */}
          <span className="n">{r.state === "not met" ? "✗" : "—"} {LABELS[r.rule] ?? r.rule}</span>
          {r.reason && <span className="why">{r.reason}</span>}
        </p>
      ))}
    </div>
  );
}

/** Every domain a team holds, sorted. Usually one; more where a team crossed a domain.
 *
 * 🔴 A MERGED TEAM BELONGS TO BOTH AND IS LISTED UNDER BOTH, which looks like double-counting
 * and is the only honest answer. Filtering to RWA and being shown fifteen of the sixteen teams
 * an RWA student is on is the question a teacher is asking; hiding the merged one because it
 * is "really" Tokenization too would drop somebody's team from the view of their own domain.
 */
/** Every domain in the plan, in the order the filter offers them. */
function domainList(p: ProposalDetail): string[] {
  return [...new Set(p.teams.flatMap((t) => domainsOf(p.questions, t)))].sort();
}

function domainsOf(questions: ProjectQuestion[], t: ProposalTeam): string[] {
  const q = questions.find((x) => x.axis === "domain");
  if (!q) return [];
  return [...new Set(t.members.map((m) => m.answers[q.id] ?? "").filter(Boolean))].sort();
}

interface Shown { id: string; label: string; value: string; kind: ProjectQuestion["kind"] }

/**
 * One member's answers, split into the three things this card does with them.
 *
 * 🔴 WHICH ANSWER STAYS VISIBLE IS READ OFF THE FORM, NOT CHOSEN HERE. A question marked
 * `share` is one the form already declares as *why these people are on a team together* — it
 * is what a teammate is shown — so the review screen surfaces the same one. A question
 * carrying an `axis` is what the arrangement SPLIT on, so it rides on the name row as a fact
 * about the placement. Everything else is behind the disclosure.
 *
 * ⚠️ AN AXIS QUESTION IS NEVER ALSO THE VISIBLE LINE. The shipped form marks Domain as both
 * `axis` and `share`, and rendering it twice would push the answer that actually says what
 * somebody wants to build below a word already on the row above it.
 * ⚠️ AN EMPTY ANSWER IS OMITTED ENTIRELY rather than rendered as a blank — the same rule the
 * grouping prompt follows, because an absence shown as a value invites reading it as one.
 */
function split(questions: ProjectQuestion[], answers: Record<string, string>):
    { axis: Shown[]; shared: Shown[]; rest: Shown[] } {
  const out = { axis: [] as Shown[], shared: [] as Shown[], rest: [] as Shown[] };
  for (const q of questions) {
    const value = answers[q.id] ?? "";
    if (!value) continue;
    const row: Shown = { id: q.id, label: q.label, value, kind: q.kind };
    if (q.axis) out.axis.push(row);
    else if (q.share) out.shared.push(row);
    else out.rest.push(row);
  }
  return out;
}

/** The owner's four rules, plus the one the form has always read.
 *
 *  🔴 EVERY RULE THE SERVER EMITS NEEDS A LABEL HERE OR IT RENDERS AS `undefined`. The union
 *  in `api.ts` is an unchecked cast of a JSON body, so `tsc` cannot catch a rule the server
 *  added and this map did not — the same two-sided-contract class that blanked this page once
 *  already. `Projects.test.tsx` asserts the two agree. */
const LABELS: Record<TeamRule["rule"], string> = {
  size: "size",
  domain: "same domain",
  picks: "picked teammates",
  seniority: "mixed experience",
  complementarity: "complementary skills",
  overlap: "hours overlap",
};

/** What the arithmetic can and cannot deliver on this cohort, from the answers alone. */
/** 🔴 WHICH PLAN IS THE CURRENT ONE — one rule, read by the table and by the tab's own
 *  auto-open. The published plan is in force whatever else exists; otherwise it is the newest.
 *  Two copies of this would let the row marked "current" be a different plan from the one the
 *  page opened, which is worse than neither saying anything. */
export function currentOf<T extends { id: string; status: string }>(rows: T[]): T | undefined {
  return rows.find((r) => r.status === "published") ?? rows[0];
}


function Proposals({ d, courseId, access, open, setOpen, onChanged }: {
  d: ProjectsOverview; courseId: string;
  access: { can_manage_teams: boolean; can_release: boolean };
  open: string; setOpen: (s: string) => void; onChanged: () => void;
}) {
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  const ask = useCallback(async (confirm: boolean) => {
    setBusy(true); setErr(null);
    try {
      const r = await requestProposal(courseId, { confirm });
      // ⚠️ A CONFIRMED REQUEST CAN STILL BE REFUSED — the cap, a run already in flight, a form
      // with no guidance. The server says so in `would_run`/`reason`, and dropping that on the
      // floor is how a button reports success for work that never started.
      if (confirm && r.would_run === false) { setPreview(r); }
      else if (confirm) { setPreview(null); onChanged(); }
      else { setPreview(r); }
    } catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  }, [courseId, onChanged]);

  // ⚠️ The same rule the tab opens on, read here rather than passed: one function, two
  // callers, so the row marked "current" is always the plan the review below is showing.
  const currentId = currentOf(d.proposals)?.id ?? "";
  // 🔴 WHAT IS BEING MADE NOW IS THE FEED'S, which moves on its own; the list is what the page
  // last read. One being made blocks another — `uq_team_proposals_one_active` refuses a second —
  // so the button says so rather than offering a press the server turns down.
  const live = useLive();
  const making = new Map((live.feed?.other ?? []).flatMap((o) =>
    o.kind === "team_grouping" ? [[o.id, o] as const] : []));
  const active = making.size > 0
    || d.proposals.some((p) => p.status === "queued" || p.status === "running");

  // 🔴 ONE BUTTON, TWO PLACES (owner, 2026-09-23): *"the request proposal button should be on
  // the line of requested table! after Cost"*. It rides in the table's own header when there
  // is a table, and under the empty state when there is not — so it is never absent, and a
  // second copy of its label or its gate cannot drift from this one.
  const request = (
    <button className="btn primary" disabled={!access.can_manage_teams || busy || active}
            onClick={() => void ask(true)}>
      {busy ? "Requesting…" : "Request team grouping"}
    </button>
  );

  return (
    // ⚠️ NO CardHead (owner, 2026-09-23). The title said "Proposals" over a table whose first
    // column is the request time, and the paragraph under it explained a run to somebody who
    // is on the page to make one. The button names the act; the table says what happened.
    <Card>
      <CardBody>
        <div className="stack">
          {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}

          {/* 🔴 ONE BUTTON, NOT A COST CHECK AND THEN A RUN (owner, 2026-09-23): *"Remove
              check how much the run would cost"*. The two-step made every run two clicks and
              a number nobody was deciding on — the daily cap is what actually bounds the
              spend, and the Cost column below reports what each run really cost.
              ⚠️ A refusal still arrives as a refusal: the server answers `would_run: false`
              with its reason, and that is what the alert renders. */}
          {preview?.would_run === false && (
            <div className="alert info" role="status">{String(preview.reason)}</div>
          )}

          {!access.can_manage_teams && (
            <p className="small muted">
              Requesting a grouping needs <b>Applicants &amp; teams</b> on this cohort.
            </p>
          )}
          {access.can_manage_teams && active && (
            <p className="small muted">
              A grouping is being made for this cohort. One runs at a time, so another can be
              requested when it ends.
            </p>
          )}

          {d.proposals.length === 0
            ? <div className="stack">
                <Empty title="No grouping has been requested yet" />
                <div className="row gap">{request}</div>
              </div>
            : (
              <div className="tablewrap">
                <table className="tb">
                  <thead>
                    <tr><th>Requested</th><th>By</th><th>State</th>
                        <th>People</th><th>Cost</th>
                        <th>{request}</th></tr>
                  </thead>
                  <tbody>
                    {/* 🔴 THE CURRENT PLAN IS MARKED AND STAYS AT THE TOP (owner,
                        2026-09-23). The rows are newest-first, so a published plan sank
                        under every later attempt — and the one in force is the one a
                        teacher is on this page about. `currentOf` is the same rule the tab
                        opens on, so the marked row and the open review cannot disagree. */}
                    {[...d.proposals].sort(
                      (a, b) => Number(b.id === currentId) - Number(a.id === currentId),
                    ).map((p) => (
                      <tr key={p.id}>
                        <td className="nowrap">
                          <When iso={p.requested_at} />
                          {p.id === currentId && <> <Badge tone="ok">current</Badge></>}
                        </td>
                        <td className="small">{p.requested_by || "—"}</td>
                        {/* ⚠️ `wrap`, OR A FAILED RUN'S SENTENCE RUNS OFF THE CARD. [X] Seen in a
                            render (2026-10-03): a cell is `nowrap` by default, so a 429's
                            explanation pushed Cost and the Request button past the right edge. */}
                        <td className="wrap">
                          <ProposalState status={p.status} now={making.get(p.id)} />
                          {p.error && <span className="small warn"> {p.error}</span>}
                        </td>
                        <td className="num">{p.eligible_count}</td>
                        <td className="num">{usd(p.cost_usd)}</td>
                        <td>
                          <button className="btn ghost sm"
                                  onClick={() => setOpen(open === p.id ? "" : p.id)}>
                            {open === p.id ? "Hide" : "Review"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          {/* 🔴 `key` REMOUNTS THE PANEL PER PROPOSAL. [X] Without it, ticking "Publish it
              anyway, knowing that" on a stale proposal and then opening a DIFFERENT one
              rendered the box already ticked — and Publish sent `accept_stale: true` for a
              proposal nobody had consented to. A pre-ticked consent box is the exact failure
              the control exists to prevent, and the commit that moved the control did not
              move the state with it. */}
          {open && <Review key={open} courseId={courseId} proposalId={open} access={access}
                           cohortName={d.course.name}
                           lo={d.form.team_size_min} onChanged={onChanged} />}
        </div>
      </CardBody>
    </Card>
  );
}

/** A proposal's state. While it is being made the feed says what it is doing and for how long;
 *  once it has ended the row is the page's own. */
function ProposalState({ status: was, now }: {
  status: string;
  now?: { status: string; started_at: string | null; requested_at: string | null };
}) {
  const status = now?.status ?? was;
  if (now || status === "queued" || status === "running") {
    return (
      <span className="rc-state">
        <Spin dim={status === "queued"} />
        {status === "queued" ? "Waiting to start" : "Reading the answers"}
        {now && <>{" "}<Elapsed since={now.started_at ?? now.requested_at} /></>}
      </span>);
  }
  return <Badge tone={status === "published" ? "ok" : status === "failed" ? "stop"
                      : status === "ready" ? "warn" : "plain"}>{status}</Badge>;
}

/** 🔴 THE PLAN UNDER REVIEW OR THE PUBLISHED ONE — the server's `CHANGEABLE`. A superseded
 *  plan is one nobody reads, and a queued or failed one has no teams to change. */
function changeable(status: string): boolean {
  return status === "ready" || status === "published";
}

/** Whether this teacher may change who is in which team on this plan.
 *
 *  🔴 ON THE PUBLISHED PLAN, RELEASE AS WELL (owner, 2026-09-28). Students are reading it, so
 *  a change reaches them straight away — the act Release has always been for. The server asks
 *  for the same pair (`routers/projects._may_change`); this only disables the controls first,
 *  because a button that answers 403 reads as the console being broken. */
function mayChange(status: string,
                   access: { can_manage_teams: boolean; can_release: boolean }): boolean {
  return changeable(status) && access.can_manage_teams
    && (status !== "published" || access.can_release);
}

function Review({ courseId, proposalId, cohortName, access, lo, onChanged }: {
  courseId: string; proposalId: string; cohortName: string;
  access: { can_manage_teams: boolean; can_release: boolean };
  /** The configured lower bound, so a team already at it can say so when you are about to
   *  take somebody out of it. */
  lo: number;
  onChanged: () => void;
}) {
  const q = useAsync(() => getProposal(courseId, proposalId), [courseId, proposalId]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [confirmStale, setConfirmStale] = useState(false);
  // ⚠️ THE DOMAIN FILTER IS A VIEW, AND IT TOUCHES NOTHING. It hides cards; publishing still
  // publishes the whole plan and the CSV still carries every team. The count beside the chips
  // is what keeps that honest — a filtered grid that looks complete is the hazard here.
  const [onlyDomain, setOnlyDomain] = useState("");
  // The team whose name is being edited. One at a time: two open editors on one page is two
  // unsaved names, and the second Save would reload the first away.
  const [naming, setNaming] = useState("");
  const sheetLabel = "Download these teams as a spreadsheet";
  // ⚠️ THE NAME SAYS WHAT COMES OUT AND WHAT DECIDES IT. "Print" alone invites pressing it
  // with a filter on and wondering where the other teams went.
  const printLabel = "Print or save as PDF — exactly the teams shown here";

  const act = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); q.reload(); onChanged(); }
    catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  }, [q, onChanged]);

  return (
    <Pane q={q} empty={<Empty title="That proposal is gone" />}>
      {(p: ProposalDetail) => (
        <div className="stack sunk">
          {/* 🔴 THE PUBLISH ACTION RIDES AT THE TOP. It sat below up to thirteen team cards,
              so acting on a review meant scrolling past everything you had just read — and
              the staleness warning that governs it was at the other end of the page. */}
          <div className="reviewbar">
            <span>
              <span className="kick">Reviewing · <When iso={p.requested_at} /></span>
              <strong>{p.teams.length} team{p.teams.length === 1 ? "" : "s"} from{" "}
                {p.eligible_count} answer{p.eligible_count === 1 ? "" : "s"}</strong>
            </span>
            <span className="spacer" />
            {/* ⚠️ AVAILABLE ON A PROPOSAL, NOT ONLY ON A PUBLISHED ONE. The file exists to be
                read while deciding, and a teacher who can only take the teams away AFTER
                telling students who they are working with has the order backwards. */}
            <button className="btn sm icon" title={sheetLabel} aria-label={sheetLabel}
                    onClick={() => {
                      const { header, body } = teamsSheet(p);
                      csv.download(csv.stamped(cohortName, "teams"),
                                   csv.toCsv(header, body));
                    }}>
              <span aria-hidden="true">⤓</span>
            </button>
            {/* 🔴 THE BROWSER'S OWN SAVE-AS-PDF, PRINTING THIS PAGE'S RULES. Drawing the
                document with a PDF library would re-implement every card and every chip, and
                the copy nobody re-renders is the one that drifts. Printing cannot disagree
                with the screen, because it IS the screen.
                ⚠️ WHAT IS ON SCREEN IS WHAT PRINTS — the domain filter and the open or closed
                disclosures included. Filter first for one domain's teams; open the answers
                you want on paper. */}
            <button className="btn sm icon" title={printLabel} aria-label={printLabel}
                    onClick={() => window.print()}>
              <span aria-hidden="true">⎙</span>
            </button>
            <State s={p.status} />
            {p.status === "ready" && (
              <button className="btn primary" disabled={!access.can_release || busy}
                      onClick={() => void act(() => publishTeams(
                        courseId, p.id, { confirm: true, accept_stale: confirmStale }))}>
                Publish to students
              </button>)}
            {/* 🔴 IN THE SAME PLACE AS PUBLISH (owner, 2026-09-23). Unpublish was at the very
                bottom of the review, several cards below the button it undoes, so the two
                halves of one decision were nowhere near each other. */}
            {p.status === "published" && (
              <button className="btn" disabled={!access.can_release || busy}
                      onClick={() => void act(() => unpublishTeams(courseId, p.id))}>
                Unpublish
              </button>)}
          </div>
          {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}

          {/* 🔴 SAID BEFORE ANYTHING IS PRESSED (owner, 2026-09-28). A published plan takes
              every change a plan under review does, and each one reaches students the moment
              it is made — which nothing else on this screen looks like it would. `status`,
              not `alert`: nothing is wrong. */}
          {p.status === "published" && (
            <div className="alert info" role="status">
              <span className="ico" aria-hidden="true">👁</span>
              {/* ⚠️ `div`s, AS EVERY OTHER ALERT HERE: two inline spans on separate lines run
                  together, because JSX drops whitespace that contains a line break. */}
              <div className="body">
                <div className="t">Students are reading this plan</div>
                <div className="m">
                  A change here reaches them straight away: whoever you move sees their new
                  team, and both teams see who left and who joined.
                </div>
              </div>
            </div>)}

          {/* 🔴 THE CONSENT BELONGS WITH THE SENTENCE IT CONSENTS TO. The warning was at the
              top and the checkbox that overrides it was at the bottom beside the button, so
              the thing being agreed to was off screen at the moment of agreeing. The label
              reads "knowing that" for the same reason — it points at the paragraph above it.
              ⚠️ AND "RUN IT AGAIN" IS THE OTHER REAL ANSWER, and had no button. */}
          {p.staleness.stale && (
            <div className="alert warn stack-tight" role="status">
              <p style={{ margin: 0 }}>
                <span className="tierchip">out of date</span>{" "}
                This plan is out of date: {p.staleness.added} arrived, {p.staleness.removed} gone
                {p.staleness.edits_only && ", and answers were edited"}. Publishing it would
                show students teams built from answers that have changed.
              </p>
              {p.status === "ready" && (
                <label className="small">
                  <input type="checkbox" checked={confirmStale}
                         onChange={(e) => setConfirmStale(e.target.checked)} />
                  {" "}Publish it anyway, knowing that
                </label>)}
            </div>
          )}

          {p.notes.length > 0 && (
            <p className="small muted">{p.notes.join(" · ")}</p>
          )}

          {/* 🔴 AN ALLOWLIST, NOT A BLOCKLIST — AND THE FIRST VERSION WAS THE WRONG ONE.
              Printing used to hide the frame by naming it: `.sidebar`, `.tabs`, `.btn` and so
              on. [X] It printed the tab strip, the whole Proposals table and the page heading
              anyway, because a blocklist only ever holds the names somebody thought of, and
              the next thing added to this page is the one it would not have been updated for.
              Everything outside this wrapper is hidden on paper; nothing has to be named. */}
          <div className="printable">
          <div className="printhead">
            {/* ⚠️ THE MARK IS ON THE PAPER, NOT ON THE SCREEN. A sheet that leaves the building
                should say whose it is; the console's own shell already does that on screen. */}
            <img src="/brand/turbin3-logo.svg" alt="Turbin3" className="printlogo" />
            <h1>{cohortName} — proposed teams</h1>
            <p>
              {p.teams.length} team{p.teams.length === 1 ? "" : "s"} from {p.eligible_count}{" "}
              answer{p.eligible_count === 1 ? "" : "s"}
              {domainList(p).length > 0 && <> · {domainList(p).join(" · ")}</>}
              {p.finished_at && <> · <When iso={p.finished_at} /></>}
            </p>
          </div>

          {/* 🔴 ALWAYS, AND ABOVE THE GRID (owner, 2026-09-23). It rendered on
              `unplaced.length > 0` and sat under sixteen team cards, so on a cohort where
              everybody is placed the one control that makes a team was absent from the page —
              and on a PUBLISHED plan it was absent as well, which is where the owner went
              looking for it: *"where we create a team??"*. It is the first thing in the review
              now, because making a team is what somebody came to this screen to do. */}
          <UnplacedPanel p={p} courseId={courseId} lo={lo} busy={busy} act={act}
                         may={mayChange(p.status, access)} />

          {/* 🔴 SIXTEEN CARDS IS NOT A LIST ANYBODY READS IN ONE PASS. A teacher reviewing a
              grouping works one domain at a time — "are the RWA teams right?" — and with
              twenty-two of forty-three people in one domain that is the only way through it.
              ⚠️ IT HIDES CARDS AND CHANGES NOTHING ELSE: publishing publishes the whole plan
              and the sheet carries every team. The count says so out loud, because a filtered
              grid that looks complete is the actual hazard. */}
          {(() => {
            const domains = [...new Set(p.teams.flatMap((t) => domainsOf(p.questions, t)))].sort();
            if (domains.length < 2) return null;
            const shownCount = p.teams.filter(
              (t) => !onlyDomain || domainsOf(p.questions, t).includes(onlyDomain)).length;
            return (
              <div className="log-filters">
                <button className={onlyDomain ? "chip" : "chip on"} aria-pressed={!onlyDomain}
                        onClick={() => setOnlyDomain("")}>All</button>
                {domains.map((dm) => {
                  const k = p.teams.filter(
                    (t) => domainsOf(p.questions, t).includes(dm)).length;
                  return (
                    <button key={dm} className={onlyDomain === dm ? "chip on" : "chip"}
                            aria-pressed={onlyDomain === dm}
                            onClick={() => setOnlyDomain(onlyDomain === dm ? "" : dm)}>
                      {dm} <span className="cnt">{k}</span>
                    </button>);
                })}
                <span className="spacer" />
                <span className="small muted">
                  {onlyDomain
                    ? `Showing ${shownCount} of ${p.teams.length} teams · publishing sends all `
                      + `${p.teams.length}`
                    : `${p.teams.length} teams`}
                </span>
              </div>);
          })()}

          <div className="grid k2">
            {p.teams
              .filter((t) => !onlyDomain || domainsOf(p.questions, t).includes(onlyDomain))
              .map((t) => (
              <Card key={t.id} className="team-card">
                <div className="thead">
                  <span className="tname">{t.name || `Team ${t.number}`}</span>
                  <span className="spacer" />
                  <span className="tcount">
                    {t.members.length} {t.members.length === 1 ? "PERSON" : "PEOPLE"}
                  </span>
                  {/* ⚠️ A STUDENT ON THE TEAM MAY HAVE CHOSEN THIS NAME, and this is how a
                      teacher changes or clears one that should not be there. Before publishing
                      it is simply a name for the plan. */}
                  {naming !== t.id && (
                    <button className="btn ghost sm" type="button"
                            disabled={!access.can_release || busy}
                            aria-label={`${t.name ? "Rename" : "Name"} team ${t.number}`}
                            onClick={() => setNaming(t.id)}>
                      {t.name ? "Rename" : "Name"}
                    </button>)}
                  {/* 🔴 ONLY ON AN EMPTY TEAM, AND THE SERVER SAYS SO TOO. A team with people
                      in it would lift them out of the plan on one click with nothing saying
                      where they went; they are moved out first, which is visible and audited.
                      ⚠️ `can_manage_teams` on a plan under review — an empty team has no
                      student on it, so removing one tells nobody anything — and Release as
                      well on the published plan, like every change to it: one rule a teacher
                      can predict beats "Release only when somebody moves". */}
                  {naming !== t.id && changeable(p.status) && t.members.length === 0 && (
                    <button className="btn ghost sm" type="button"
                            disabled={!mayChange(p.status, access) || busy}
                            aria-label={`Remove team ${t.number}`}
                            onClick={() => void act(async () => {
                              await removeTeam(courseId, p.id, t.id);
                            })}>
                      Remove
                    </button>)}
                </div>
                <div className="tbody">
                  {naming === t.id && (
                    <TeamNameForm number={t.number} name={t.name}
                                  hint={p.status === "published"
                                    ? "The students on this team see the new name straight away."
                                    : "Students see it once this plan is published."}
                                  onSave={async (name) => {
                                    await renameTeam(courseId, p.id, t.id, name);
                                    q.reload(); onChanged();
                                  }}
                                  onClose={() => setNaming("")} />)}
                  <TeamRules rules={t.rules} />
                  {/* 🔴 THE MODEL'S READ OF THE TEAM, AND IT IS ABOUT ALL OF THEM. It was the
                      card's `desc` — 12.5px grey caption under the title, where it reads as
                      decoration rather than as the one sentence saying whether this grouping
                      holds together. Labelled, because a block of prose on a card otherwise
                      reads as something a student wrote. */}
                  {t.rationale && (
                    <div className="tmodel">
                      <span className="k">The model&rsquo;s read of this team</span>
                      <p>{t.rationale}</p>
                    </div>
                  )}
                  <ul className="applist">
                    {t.members.map((m) => (
                      <li key={m.intent_id} className="mate">
                        <span className="row wrap gap" style={{ alignItems: "center" }}>
                          <strong>{m.display_name}</strong>
                          {m.placed_by_hand
                            ? (
                              /* ⚠️ WHY THEIR TEAM'S RULES READ `not measured`. Nobody read
                                 them: they were placed after the run, so there is no level
                                 and no tags for complementarity to work with. */
                              <Badge tone="held">placed by hand</Badge>)
                            : (
                              <Badge tone={
                                m.level === "strong" ? "ok"
                                : m.level === "intermediate" ? "plain" : "held"}>
                                {m.level}</Badge>)}
                          {/* ⚠️ THE AXIS ANSWERS RIDE ON THE NAME ROW, because they are what
                              the arrangement SPLIT on — a teacher checking a team reads them
                              as facts about the placement, not as answers to look up. */}
                          {split(p.questions, m.answers).axis.length > 0 && (
                            <span className="small muted">
                              {split(p.questions, m.answers).axis
                                .map((a) => a.value).join(" \u00b7 ")}
                            </span>
                          )}
                        </span>
                        {/* 🔴 ONE ANSWER VISIBLE, AND WHICH ONE IS NOT A GUESS. The form marks
                            a question `share` to mean "a teammate may see this" — which it
                            does precisely because it is what says why these people are on a
                            team together. So the review screen shows the same one. Everything
                            else goes behind the disclosure: six answers a person, one of them
                            up to 500 characters, across sixteen cards is not a screen anybody
                            reads.
                            ⚠️ CLAMPED TO TWO LINES RATHER THAN TRUNCATED IN THE STRING, so
                            the whole answer is still selectable and still in the page for a
                            find-in-page. */}
                        {split(p.questions, m.answers).shared.map((a) => (
                          <p key={a.id} className="midea">{a.value}</p>
                        ))}
                        {split(p.questions, m.answers).rest.length > 0 && (
                          <details className="mrest">
                            <summary>
                              {split(p.questions, m.answers).rest.length} more answer
                              {split(p.questions, m.answers).rest.length === 1 ? "" : "s"}
                            </summary>
                            <div className="manswers">
                              {split(p.questions, m.answers).rest.map((a) => (
                                <div key={a.id} className="a">
                                  <span className="ql">{a.label}</span>
                                  {a.kind === "link"
                                    ? <a className="v brk" href={a.value} target="_blank"
                                         rel="noreferrer noopener">{a.value}</a>
                                    : <span className="v">{a.value}</span>}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                        {m.picks.length > 0 && (
                          <p className="mpicks">
                            <span className="k">Picked</span>{" "}
                            {m.picks.map((x, n) => (
                              <span key={n} className={`mpick${x.team === t.number ? " here" : ""}`}>
                                {x.name}{x.mutual && <span title="picked each other"> ↔</span>}
                                {x.team !== t.number && (
                                  <span className="muted">
                                    {x.team ? ` · team ${x.team}` : " · not in this plan"}
                                  </span>)}
                              </span>))}
                          </p>)}
                        {m.rationale && (
                          <p className="mwhy">Model: {m.rationale}</p>)}
                        {changeable(p.status) && (
                          <MoveTo teams={p.teams} here={t.id} lo={lo}
                                  who={{ id: m.intent_id, name: m.display_name }}
                                  published={p.status === "published"}
                                  disabled={!mayChange(p.status, access) || busy}
                                  onMove={(to) => void act(() => moveMember(
                                    courseId, p.id, m.intent_id, to))} />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>
          </div>

          {/* 🔴 EVERYBODY WITH NO TEAM, AND SOMETHING TO DO ABOUT THEM (owner, 2026-09-22).
              The review was built from the memberships alone, so these people were a number in
              the staleness line — "3 intent(s) have arrived" — and the only remedy was paying
              for a whole new grouping that re-arranges everybody else too. */}

          {/* ⚠️ NO SECOND PUBLISH HERE. It rides in the sticky header now; two buttons doing
              one thing is how somebody presses the one that is not disabled. */}
          <div className="row gap">
            {!access.can_release && (
              <span className="small muted">
                Publishing tells students who they are working with, and a team&rsquo;s name is
                on their page, so publishing and naming teams need <code>can_release</code>.
              </span>
            )}
            {/* ⚠️ SAID HERE, NOT ONLY ABOVE THE TABLE. The line up there is about requesting,
                and a disabled Move sits several cards below it with no reason of its own. */}
            {!access.can_manage_teams && changeable(p.status) && (
              <span className="small muted">
                Moving somebody between teams needs <b>Applicants &amp; teams</b> on this
                cohort.
              </span>
            )}
            {/* ⚠️ THE SECOND HALF OF `mayChange`, which the line above cannot say: this
                teacher builds plans, and the published one is the one they may not touch. */}
            {p.status === "published" && access.can_manage_teams && !access.can_release && (
              <span className="small muted">
                This plan is published, so changing who is in which team needs Release as well
                as <b>Applicants &amp; teams</b> — a change reaches students straight away.
              </span>
            )}
          </div>
        </div>
      )}
    </Pane>
  );
}

/**
 * The people on this cohort with no team on this plan, and the two things to do about them.
 *
 * 🔴 TICKED BY STUDENT, NEVER BY ANSWER ROW. Somebody who never answered has no answer row
 * until a placement writes one (`0045`), so the student id is the only identifier both kinds
 * of person have — and one identifier is what keeps this a single list rather than two
 * half-supported ones.
 *
 * ⚠️ THE TICKS LIVE HERE, so a reload of the proposal does not carry a half-made selection
 * into a plan that has changed underneath it.
 */
/** 🔴 A BUTTON AND A POPUP, NOT A FORM LYING ON THE CARD (owner, 2026-09-23): *"souhld be a
 *  abutton new time and than a poupup, not this"*. The card's job is to REPORT who has no
 *  team; making one is an act with its own fields, and spreading those across the card put a
 *  name box and two disclosures in front of a reader who only wanted to see who was left out.
 *
 *  ⚠️ A PLAIN OVERLAY, NOT `<dialog>`. `showModal` gives a real focus trap for free and jsdom
 *  supports it unevenly, so the one place this app needs a modal is built the way its other
 *  popovers are — Escape, a click on the scrim, and the focus moved to the first field. */
/**
 * 🔴 PLACED FROM THE BUTTON'S OWN RECTANGLE, `fixed` — the Why panel's answer to the same
 * problem. [X] Rendered 2026-09-22: absolutely positioned, the menu opened at the far edge of
 * the entry (`.mate` is a grid, which stretched this wrapper) and then, once it carried names,
 * was cut off by the card's `overflow: hidden`. It opens UPWARDS when there is no room below,
 * because it closes on scroll and a menu off the bottom of the window could not be reached.
 * ⚠️ UNLIKE THE WHY PANEL IT SCROLLS, so a scroll from inside it does not close it.
 */
/** The room the Move menu needs under its button before it opens upwards instead. */
const MOVE_ROOM = 320;


function NewTeamDialog({ p, courseId, busy, act, onClose }: {
  p: ProposalDetail; courseId: string; busy: boolean;
  act: (fn: () => Promise<unknown>) => void; onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const first = useRef<HTMLInputElement>(null);
  // ⚠️ THE NUMBER THE SERVER WILL GIVE IT, so the placeholder shows the name it gets if the
  // teacher types none — "the default as the others" is `Team N`, not an empty card.
  const next = Math.max(0, ...p.teams.map((t) => t.number)) + 1;

  useEffect(() => {
    first.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const toggle = (id: string, on: boolean) => setTicked((was) => {
    const next2 = new Set(was);
    if (on) next2.add(id); else next2.delete(id);
    return next2;
  });

  const pick = (id: string, who: string, note: string) => (
    <li key={id} className="unplaced-row">
      <label className="unplaced-pick">
        <input type="checkbox" checked={ticked.has(id)} disabled={busy}
               onChange={(e) => toggle(id, e.currentTarget.checked)} />
        <span className="unplaced-who">
          <strong>{who}</strong>
          <span className="small muted">{note}</span>
        </span>
      </label>
    </li>
  );

  const placed = p.teams.flatMap((t) => t.members.map((m) => ({ m, t })));

  return (
    <div className="scrim"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="New team">
        <div className="modal-head">
          <strong>New team</strong>
          <span className="spacer" />
          <button className="btn ghost sm" type="button" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          {/* ⚠️ ON THE PUBLISHED PLAN THIS IS NOT A DRAFT, and the popup is where a split
              happens — so it says so before anybody is ticked. */}
          {p.status === "published" && (
            <p className="small muted">
              This plan is published: the people you tick see their new team straight away.
            </p>)}
          {/* ⚠️ A NAME IS OPTIONAL AND ITS PLACEHOLDER IS WHAT IT GETS WITHOUT ONE. */}
          <label className="unplaced-name">
            <span className="small muted">Name</span>
            <input className="input" value={name} maxLength={80} ref={first}
                   placeholder={`Team ${next}`} disabled={busy}
                   onChange={(e) => setName(e.currentTarget.value)} />
          </label>

          {p.unplaced.length > 0 && (
            <>
              <p className="small muted">No team yet</p>
              <ul className="unplaced">
                {p.unplaced.map((u) => pick(u.student_id, u.display_name, u.why))}
              </ul>
            </>
          )}

          {/* 🔴 PEOPLE WHO ARE ALREADY IN A TEAM (owner, 2026-09-23): *"the members even if
              other teams"*. Splitting one team into two is the case, and it travels in ONE
              call — creating the team and then moving people one at a time leaves a half-made
              team behind the moment something refuses. Ticking one MOVES them: the server
              deletes the old membership in the same transaction. */}
          {placed.length > 0 && (
            <>
              <p className="small muted">Already in a team — ticking moves them out of it</p>
              <ul className="unplaced">
                {placed.map(({ m, t }) =>
                  pick(m.student_id, m.display_name, `in ${t.name || `Team ${t.number}`}`))}
              </ul>
            </>
          )}
        </div>
        <div className="modal-foot">
          <span className="small muted">
            {ticked.size
              ? `${ticked.size} selected`
              : "It starts empty — you can move people into it afterwards."}
          </span>
          <span className="spacer" />
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          {/* 🔴 ENABLED WITH NOBODY TICKED (owner, 2026-09-23): *"even if no users available,
              than we move them"*. Splitting one team into two starts with a team that has
              nobody in it, and there is nobody to tick for that. */}
          <button className="btn primary" disabled={busy}
                  onClick={() => void act(async () => {
                    await createTeam(courseId, p.id, [...ticked], name.trim());
                    onClose();
                  })}>
            Create team
          </button>
        </div>
      </div>
    </div>
  );
}


function UnplacedPanel({ p, courseId, lo, busy, act, may }: {
  p: ProposalDetail; courseId: string; lo: number; busy: boolean;
  act: (fn: () => Promise<unknown>) => void;
  /** `mayChange` for this teacher on this plan. ⚠️ Before this, New team was gated on `busy`
   *  alone, so a teacher who may only look was offered a button the server would refuse. */
  may: boolean;
}) {
  const [making, setMaking] = useState(false);
  // 🔴 THE PUBLISHED PLAN TOO (owner, 2026-09-28). This was `ready` alone, and said "Unpublish
  // this plan to put people into teams" — so placing one late arrival meant taking every team
  // off every student's page first.
  const open = changeable(p.status);
  const answered = p.unplaced.filter((u) => u.answered);
  const silent = p.unplaced.filter((u) => !u.answered);

  // ⚠️ THE CARD REPORTS, THE POPUP ACTS. These rows carry no checkbox: picking who goes in a
  // new team happens in the dialog, and a row here is a person the plan left out plus the one
  // thing to do about them on the spot.
  const person = (u: Unplaced) => {
    const axis = split(p.questions, u.answers).axis.map((a) => a.value).join(" \u00b7 ");
    return (
      <li key={u.student_id} className="unplaced-row">
        <span className="unplaced-who">
          <strong>{u.display_name}</strong>
          {axis && <span className="small muted">{axis}</span>}
          <span className="small muted">{u.why}</span>
        </span>
        {/* ⚠️ `here=""` — they are in no team, so every team in the menu is somewhere to go. */}
        {open && (
          <MoveTo teams={p.teams} here="" lo={lo} who={{ id: "", name: u.display_name }}
                  published={p.status === "published"}
                  disabled={!may || busy}
                  onMove={(to) => void act(
                    () => addToTeam(courseId, p.id, to, [u.student_id]))} />
        )}
      </li>
    );
  };

  return (
    <Card>
      <CardHead title={p.unplaced.length ? `No team yet (${p.unplaced.length})` : "Make a team"}
                desc={p.unplaced.length
                  ? "On this cohort, and in no team on this plan."
                  : "Everybody on this cohort is in a team. A new one starts empty."}
                right={open ? (
                  <button className="btn primary" disabled={!may || busy}
                          onClick={() => setMaking(true)}>New team</button>
                ) : undefined} />
      <CardBody>
        {!open && (
          <p className="small muted">
            Only the plan under review or the published one can be changed.
          </p>)}
        {answered.length > 0 && <ul className="unplaced">{answered.map(person)}</ul>}
        {/* ⚠️ BEHIND A COUNT, BECAUSE THEY ARE THE LONG LIST AND THE RARER JOB. On a cohort
            where 43 of 103 answered this is sixty names, and putting them first would bury
            the three people a teacher came here for. */}
        {silent.length > 0 && (
          <details className="unplaced-more">
            <summary>
              {silent.length === 1 ? "1 who has not answered"
                : `${silent.length} who have not answered`}
            </summary>
            <ul className="unplaced">{silent.map(person)}</ul>
          </details>
        )}
        {making && (
          <NewTeamDialog p={p} courseId={courseId} busy={busy} act={act}
                         onClose={() => setMaking(false)} />
        )}
      </CardBody>
    </Card>
  );
}


function MoveTo({ teams, here, lo, who, published, disabled, onMove }: {
  teams: ProposalDetail["teams"]; here: string; lo: number;
  who: { id: string; name: string };
  /** Whether this is the plan students are reading, which decides what the foot says. */
  published: boolean;
  disabled: boolean;
  onMove: (to: string) => void;
}) {
  const [at, setAt] = useState<{ top?: number; bottom?: number; left: number;
                                 room: number } | null>(null);
  const open = at !== null;
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  const show = () => {
    const r = button.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < MOVE_ROOM && above > below;
    setAt({ ...(up ? { bottom: window.innerHeight - r.top + 5 } : { top: r.bottom + 5 }),
            left: Math.max(12, Math.min(r.left, window.innerWidth - 332)),
            room: up ? above : below });
  };

  // Only while open: thirty-eight people meant thirty-eight idle document listeners.
  useEffect(() => {
    if (!open) return;
    const close = () => setAt(null);
    // ⚠️ A SCROLL CAN BE FIRED AT `window`, WHICH IS NOT A NODE, and `contains` throws on one —
    // so the listener died before it could close anything. Not a node is outside.
    const outside = (e: Event) =>
      !(e.target instanceof Node && wrap.current?.contains(e.target));
    const onScroll = (e: Event) => { if (outside(e)) close(); };
    const onDown = (e: Event) => { if (outside(e)) close(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className="moveto" ref={wrap}>
      <button ref={button} className={open ? "btn ghost sm on" : "btn ghost sm"}
              disabled={disabled} aria-haspopup="menu" aria-expanded={open}
              onClick={() => (open ? setAt(null) : show())}>Move →</button>
      {at && (
        <span className="moveto-pop" role="menu"
              style={{ top: at.top, bottom: at.bottom, left: at.left }}>
          <span className="moveto-head">Move <b>{who.name}</b> to</span>
          {/* The heading and the foot take about 90px of whatever room there is. */}
          <span className="moveto-list"
                style={{ maxHeight: Math.max(120, Math.min(300, at.room - 90)) }}>
            {teams.map((o) => {
              const mine = o.id === here;
              const small = o.members.length <= lo;
              const names = o.members.filter((x) => x.intent_id !== who.id)
                .map((x) => x.display_name);
              return (
                <button key={o.id} role="menuitem" className="moveto-row" disabled={mine}
                        onClick={() => { onMove(o.id); setAt(null); }}>
                  <span className="moveto-n">{o.number}</span>
                  <span className="moveto-team">
                    <span className="moveto-name">{o.name || `Team ${o.number}`}</span>
                    {names.length > 0 && (
                      <span className="moveto-who">{names.join(" · ")}</span>)}
                  </span>
                  {mine
                    ? <span className="tierchip">here</span>
                    : <span className={small ? "moveto-size warn" : "moveto-size"}>
                        {o.members.length}</span>}
                </button>);
            })}
          </span>
          {/* ⚠️ IT SAID "not the published plan" EVERYWHERE — false the moment a published
              plan could take a move (2026-09-28). */}
          <span className="moveto-foot">
            {published
              ? "Published: the students on both teams see this straight away."
              : "A move edits this proposal, not the published plan."}
          </span>
        </span>)}
    </span>
  );
}


/** A row of picked names, marking the mutual ones. */
function PickNames({ picks }: { picks: { name: string; mutual: boolean }[] }) {
  return (
    <span className="aval">
      {picks.map((x, n) => (
        <span key={n} className="mpick">
          {x.name}{x.mutual && <span title="picked each other"> ↔</span>}
        </span>))}
    </span>
  );
}
